import React, { useState, useRef, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FaCheck, FaEraser, FaPaperPlane, FaDownload, } from 'react-icons/fa';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";
import UserContext from '../../../components/User_Context.js';
import * as api from "../../../api/apiConnector.js";
import * as uploadManager from "../../../components/ui/fileupload.js";
import * as icons from "../../../components/fileTypesIcons.js";
import waiting from "../../../assets/images/waiting.svg";
import folderSearch from '../../../assets/icon/folder-search.svg';
import cloud_icon from "../../../assets/images/cloud.svg";
import * as breadcrumb from "../../../components/ui/breadcrumb.js";
import Choices from '../../../components/Choices.js';
import { formBuilderLabels } from '../../_label/form_builder/form_builder_labels.js';

// Import new components for form rendering
import FormSection from './components/FormSection.js';
import AttachmentArea from './components/AttachmentArea.js';
import FileUploadUI from './components/FileUploadUI.js';
import LinkUI from './components/LinkUI.js';
import TextUI from './components/TextUI.js';
import ObservationUI from './components/ObservationUI.js';
import ImageZoomModal from './components/ImageZoomModal.js';
import UserGuideDisplay from './components/UserGuideDisplay.js';
import ContextDataDisplay from './components/ContextDataDisplay.js';
import useFormData from './hooks/useFormData.js';
import EditorJSRenderer from './components/EditorJSRenderer.js';
import QuestionValidationRules from './components/QuestionValidationRules.js';
import ValidationErrorDisplay from './components/ValidationErrorDisplay.js';
import { validateQuestion, getDisabledAnswers } from './utils/questionValidation.js';

// Default color palette for the application
const defaultColors = {
  // Primary brand colors
  primary: '#f05f23', // Orange
  primaryHover: '#d44d12',
  primaryLight: '#fef0ea',

  // Secondary neutral colors
  secondary: '#6c757d',
  secondaryLight: '#e9ecef',

  // Base colors
  dark: '#343a40',
  light: '#f8f9fa',
  white: '#ffffff',

  // Alert colors for different states
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8'
};

/**
 * Unified Form Compiler Component
 * 
 * This component handles both simulation and compilation modes for forms:
 * - Simulation mode: Used for testing forms without saving data
 * - Compilation mode: Used for actual form completion with data persistence
 * 
 * The component dynamically renders different UI elements based on the mode
 * and provides comprehensive form interaction capabilities including:
 * - Multiple question types (single, multiple, open, date, vocabulary, etc.)
 * - File attachments with drag & drop functionality
 * - Form validation and submission
 * - Visibility conditions for questions and sections
 */
const Form_Compiler = (props) => {
  // Determine the operating mode (simulation vs compilation)
  const isSimulation = props.mode === 'simulation';

  // Simulation: which actor's perspective to simulate (null = use first actor)
  const [simulationActorId, setSimulationActorId] = useState(null);

  // Extract props with default values
  const { form: propForm, formCompletion: propFormCompletion, language: propLanguage, onClose, colors = defaultColors, readOnly = false, showScores = false } = props;

  // Router hooks (only used in compilation mode for URL management)
  const { formId, completionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const readOnlyFromUrl = searchParams.get('readOnly') === 'true';
  const effectiveReadOnly = readOnly || readOnlyFromUrl;

  // Use custom hook for form data management
  // This centralizes all form state and data loading logic
  const {
    loading,
    form,
    formCompletion,
    setFormCompletion,
    formValues,
    setFormValues,
    answerFiles,
    setAnswerFiles,
    answerLinks,
    setAnswerLinks,
    answerObservations,
    setAnswerObservations,
    answerTexts,
    setAnswerTexts,
    activeAttachmentTab,
    setActiveAttachmentTab,
    answersFolderId,
    preventQuit,
    setPreventQuit,
    vocabularyItems,
    customVocabularyItems,
    departmentsVoc,
    functionalAreaVocs,
    getDescendants,
    loadFormData,
    MySwal,
    currentUser,
    collaborativeFormsSchema,
    workflowInfo,
    formAssignmentId,
    collaborativeAssignmentId,
    lockState,
    isCollaborative
  } = useFormData(isSimulation, propForm, onClose, colors, propFormCompletion, simulationActorId);

  // Additional state variables for UI management
  const [formKey, setFormKey] = useState(1); // Used to force form reset by changing key
  const [language, setLanguage] = useState(propLanguage || 'it'); // Current language setting
  const [zoomImageUrl, setZoomImageUrl] = useState(null); // For image zoom modal
  const [currentQuestionId, setCurrentQuestionId] = useState(null); // Context for file uploads
  // Track collapsed state per section (keyed by section.id or index)
  const [collapsedSections, setCollapsedSections] = useState({});
  // Track validation errors per question (keyed by questionId)
  const [validationErrors, setValidationErrors] = useState({});
  // Track loaded authenticated images (keyed by file.Id)
  const [loadedImages, setLoadedImages] = useState({});
  // Track user guide collapse state with localStorage persistence
  const [isGuideCollapsed, setIsGuideCollapsed] = useState(() => {
    // Load user preference from localStorage, default to false (expanded)
    const savedState = localStorage.getItem('userGuideCollapsed');
    return savedState === 'true';
  });
  const [isContextCollapsed, setIsContextCollapsed] = useState(false);
  // Track toolbar height for responsive sticky positioning
  const [toolbarHeight, setToolbarHeight] = useState(0);

  // References for DOM manipulation
  const formRef = useRef(null); // Reference to the main form element
  const dragAnimationRef = useRef({ stopSignal: false }); // Controls drag animation state

  // Internationalization hook
  const { t } = useTranslation();

  /**
   * Determines if a question should be readonly based on collaborative forms schema
   * Now also considers section-level assignments in addition to individual question assignments
   *
   * @param {string} questionId - The ID of the question to check
   * @returns {boolean} True if the question should be readonly (not assigned to current user)
   */
  const isQuestionReadOnlyCollaborative = (questionId) => {
    // If no collaborative schema or empty, all questions are editable
    if (!collaborativeFormsSchema || collaborativeFormsSchema.length === 0) {
      return false;
    }

    // Extract all question IDs that are assigned to the current user
    // This includes both:
    // 1. Questions assigned directly (in actor.questions array)
    // 2. Questions assigned indirectly through section assignment
    const assignedQuestionIds = collaborativeFormsSchema.flatMap(actor =>
      actor.questions || []
    );

    // If there are assigned questions, only those are editable (all others are readonly)
    // Return true (readonly) if this question is NOT in the assigned list
    // Note: The questions array already includes all questions from assigned sections,
    // so we don't need to check sections separately here
    return !assignedQuestionIds.includes(questionId);
  };

  /**
   * Determines if a specific answer should be readonly for multiple-choice/PDCA questions
   * This allows granular control where different actors can edit different answers
   *
   * @param {string} questionId - The ID of the question
   * @param {string} answerId - The ID of the answer to check
   * @returns {boolean} True if the answer should be readonly (not assigned to current user)
   */
  const isAnswerReadOnlyCollaborative = (questionId, answerId) => {
    // If no collaborative schema or empty, all answers are editable
    if (!collaborativeFormsSchema || collaborativeFormsSchema.length === 0) {
      return false;
    }

    // Check if the question has answer-level assignments
    const hasAnswerAssignments = collaborativeFormsSchema.some(actor =>
      actor.answers && actor.answers[questionId] && actor.answers[questionId].length > 0
    );

    // If there are no answer-level assignments for this question, fall back to question-level logic
    if (!hasAnswerAssignments) {
      return isQuestionReadOnlyCollaborative(questionId);
    }

    // Extract all answer IDs assigned to the current user for this specific question
    const assignedAnswerIds = collaborativeFormsSchema.flatMap(actor =>
      actor.answers?.[questionId] || []
    );

    // Convert to strings for comparison to avoid type mismatch
    const assignedAnswerIdsStr = assignedAnswerIds.map(id => String(id));
    const answerIdStr = String(answerId);

    // Debug log
    console.log('isAnswerReadOnlyCollaborative:', {
      questionId,
      answerId: answerIdStr,
      assignedAnswerIds: assignedAnswerIdsStr,
      isReadOnly: !assignedAnswerIdsStr.includes(answerIdStr)
    });

    // Return true (readonly) if this answer is NOT in the assigned list
    return !assignedAnswerIdsStr.includes(answerIdStr);
  };

  /**
   * GUID Generator Function
   * 
   * Generates unique identifiers for user input answers
   * Used for open text, number, date/time, and department/functional area questions
   * 
   * @returns {Promise<string|null>} Generated GUID or null if failed
   */
  const getGuid = async () => {
    try {
      const result = await api.sendMessage("helper/guidGenerator", {
        user: currentUser(),
        body: {}
      });

      if (result.success && result.guid) {
        return result.guid;
      }
      console.error("Error getting GUID:", result);
      return null;
    } catch (err) {
      console.error("Error calling GUID generator:", err);
      return null;
    }
  };

  /**
   * Component Initialization Effect
   * 
   * Sets up the component based on operating mode:
   * - Compilation mode: Initializes menu system
   * - Simulation mode: No special initialization needed
   */
  useEffect(() => {
    // Setup menu for compilation mode only
    if (!isSimulation) {
      (async () => {
        menu.write({ user: currentUser() });
      })();
    }
  }, [isSimulation]);

  /**
   * Toolbar Height Calculation Effect
   * 
   * Dynamically calculates the toolbar height for responsive sticky positioning
   * This ensures the user guide doesn't overlap with the toolbar on any screen size
   * In simulation mode and read-only mode, uses minimal offset since there's no toolbar
   */
  useEffect(() => {
    const calculateToolbarHeight = () => {
      // In read-only mode or simulation mode, there's no toolbar, so use minimal offset
      if (effectiveReadOnly || isSimulation) {
        setToolbarHeight(20);
        return;
      }

      // Try to find the toolbar element
      const headerElement = document.querySelector('header');
      if (headerElement) {
        const height = headerElement.offsetHeight;
        // Add more padding (40px) to ensure complete separation from toolbar
        // This accounts for potential shadows, borders, and visual spacing
        setToolbarHeight(height + 40);
      } else {
        // Fallback to calculated height based on CSS (3.125rem = 50px + generous padding)
        const baseFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const headerHeightRem = 3.125; // From CSS
        const calculatedHeight = (headerHeightRem * baseFontSize) + 40; // Add generous padding
        setToolbarHeight(calculatedHeight);
      }
    };

    // Calculate initially with a delay to ensure DOM is ready
    const initialTimeout = setTimeout(calculateToolbarHeight, 200);

    // Recalculate on window resize to handle responsive changes
    const handleResize = () => {
      // Debounce resize events
      clearTimeout(window.resizeTimeout);
      window.resizeTimeout = setTimeout(calculateToolbarHeight, 100);
    };

    window.addEventListener('resize', handleResize);

    // Also recalculate when scroll happens (in case of dynamic toolbars)
    const handleScroll = () => {
      // Only recalculate occasionally during scroll to avoid performance issues
      if (!window.scrollRecalculating) {
        window.scrollRecalculating = true;
        setTimeout(() => {
          calculateToolbarHeight();
          window.scrollRecalculating = false;
        }, 500);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(window.resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [effectiveReadOnly]);



  /**
   * Internationalization Setup Effect
   * 
   * Adds translation resources for both Italian and English languages
   * Also sets up drag & drop styling and event handlers
   */
  useEffect(() => {
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);

    // Add CSS for drag & drop functionality
    const dragStyleId = isSimulation ? 'sim-dnd-styles' : 'comp-dnd-styles';
    const dragClassPrefix = isSimulation ? 'sim' : 'comp';

    if (!document.getElementById(dragStyleId)) {
      const styleSheet = document.createElement('style');
      styleSheet.id = dragStyleId;
      styleSheet.innerHTML = `
        .${dragClassPrefix}-draggable-area {
            border: 2px dashed #007bff !important;
            background-color: rgba(0, 123, 255, 0.1) !important;
            transition: all 0.3s ease;
            box-shadow: 0 0 10px rgba(0, 123, 255, 0.2) !important;
            transform: scale(1.01);
        }
        
        .${dragClassPrefix}-draggable-container {
            border: 2px dashed #ccc;
            border-radius: 8px;
            min-height: 90px;
            transition: all 0.3s ease;
            background-color: #f8f9fa;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            cursor: pointer;
        }
        
        .${dragClassPrefix}-draggable-container:hover {
            border-color: #adb5bd;
            background-color: #f1f3f5;
        }
      `;
      document.head.appendChild(styleSheet);
    }

    // Setup global drag & drop event handlers
    const handleGlobalDragEnd = () => {
      cleanupAllDragHighlights();
    };

    document.addEventListener('dragend', handleGlobalDragEnd);
    document.addEventListener('drop', handleGlobalDragEnd);

    // Handle drag operations leaving the document area
    const handleDocumentDragLeave = (e) => {
      const rect = document.documentElement.getBoundingClientRect();
      if (
        e.clientX <= rect.left ||
        e.clientX >= rect.right ||
        e.clientY <= rect.top ||
        e.clientY >= rect.bottom
      ) {
        cleanupAllDragHighlights();
      }
    };

    document.addEventListener('dragleave', handleDocumentDragLeave);

    // Cleanup function to remove event listeners and styles
    return () => {
      const styleSheet = document.getElementById(dragStyleId);
      if (styleSheet) {
        document.head.removeChild(styleSheet);
      }

      document.removeEventListener('dragend', handleGlobalDragEnd);
      document.removeEventListener('drop', handleGlobalDragEnd);
      document.removeEventListener('dragleave', handleDocumentDragLeave);
    };
  }, [isSimulation]);

  // Cleanup blob URLs when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clean up all blob URLs when component unmounts
      Object.values(loadedImages).forEach(url => {
        if (url && url.startsWith('blob:')) {
          window.URL.revokeObjectURL(url);
        }
      });
    };
  }, [loadedImages]);

  /**
   * Drag & Drop Cleanup Function
   * 
   * Removes all visual drag highlighting from draggable containers
   * Called when drag operations end or leave the document
   */
  const cleanupAllDragHighlights = () => {
    const dragClassPrefix = isSimulation ? 'sim' : 'comp';
    // Remove all drag area highlighting classes
    window.$(`.${dragClassPrefix}-draggable-container`).removeClass(`${dragClassPrefix}-draggable-area`);

    // Hide the floating drag indicator bubble
    hideDragBubble();
  };

  /**
   * Hide Drag Bubble Function
   * 
   * Hides the floating drag indicator with animation
   * Signals animation loops to stop and removes the bubble element
   */
  const hideDragBubble = () => {
    // Signal to stop any ongoing animations
    dragAnimationRef.current.stopSignal = true;

    const bubbleId = isSimulation ? "sim-dragging-bubble" : "comp-dragging-bubble";
    const bubbleIconId = isSimulation ? "sim-dragging-bubble-icon" : "comp-dragging-bubble-icon";

    const _bubble = window.$(`#${bubbleId}`);
    if (_bubble.length > 0) {
      const _icon = window.$(`#${bubbleIconId}`);
      if (_icon.length > 0) {
        _icon.stop(); // Stop any ongoing animations
        _icon.animate({ "top": "-=800px" }, "medium", () => {
          _bubble.remove();
        });
      } else {
        _bubble.remove();
      }
    }
  };

  /**
   * Drag Effect Animation Function
   * 
   * Creates and animates a floating bubble indicator during drag operations
   * Shows visual feedback to users about where they can drop files
   * 
   * @param {Event} e - The drag event
   * @param {boolean} show - Whether to show or hide the effect
   * @param {string} questionId - ID of the question being targeted
   */
  const _frDraggingEffect = (e, show, questionId) => {
    // Don't show new bubble if we're hiding
    if (!show) {
      return; // hideDragBubble is called elsewhere when needed
    }

    // Remove any existing bubble before showing a new one
    hideDragBubble();

    // Reset animation state
    dragAnimationRef.current.stopSignal = false;

    const bubbleId = isSimulation ? "sim-dragging-bubble" : "comp-dragging-bubble";
    const bubbleIconId = isSimulation ? "sim-dragging-bubble-icon" : "comp-dragging-bubble-icon";

    // Create floating bubble with drop zone information
    const _panel = window.$(`            
      <div id="${bubbleId}" class="bg-primary"             
      style="box-shadow: 2px 2px 2px 1px rgb(0 0 0 / 20%); z-index: 10000; position: fixed; bottom: 20px; transform: translateX(-50%); left: 50%; width: 250px; height: 70px; border-radius: 20px; opacity: .1;">
      <div id="${bubbleIconId}" style="position: absolute; margin-top: -60px; width: 100%; text-align: center;"><img style="width: 80px" src="${cloud_icon}" /></div>
      <div style="position: relative; left: 0; top: 20px; width: 100%; text-align: center; color: white;">${t('dropFileHere', { ns: 'labels' })}</div>
      <div style="padding: 20px; position: relative; left: 0; top: 5px; width: 100%; text-align: center; color: white; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;"><i class="fa fa-folder-tree me-2"></i> ${t('uploadFile', { ns: 'labels' })}</div>
      </div>`);

    // Show the bubble with fade-in animation
    window.$("body").append(_panel);
    _panel.fadeTo("slow", 1);

    let _direction = "up";

    // Continuous animation loop for the floating icon
    const _animate = () => {
      const _icon = window.$(`#${bubbleIconId}`);
      if (!_icon.length) return; // Exit if element no longer exists

      if (_direction === "up") {
        _icon.animate({ "top": "-=20px" }, "slow", () => {
          _direction = "down";
          if (!dragAnimationRef.current.stopSignal && window.$(`#${bubbleId}`).length > 0) _animate();
        });
      } else {
        _icon.animate({ "top": "+=20px" }, "slow", () => {
          _direction = "up";
          if (!dragAnimationRef.current.stopSignal && window.$(`#${bubbleId}`).length > 0) _animate();
        });
      }
    }

    _animate();
  };

  /**
   * Text Localization Helper Function
   * 
   * Retrieves text in the current language from a multilingual array
   * Falls back to any available language or default text if current language not found
   * 
   * @param {Array} textArray - Array of text objects with language and text properties
   * @param {string} defaultText - Fallback text if no localized version found
   * @returns {string} The localized text or fallback
   */
  const getLocalizedText = (textArray, defaultText) => {
    // Return default if no array or empty array
    if (!textArray || !Array.isArray(textArray) || textArray.length === 0) return defaultText;

    // Look for text in current language
    const localizedItem = textArray.find(t => t && t.language === language);

    // If not found or empty, try any available language as fallback
    if (!localizedItem || !localizedItem.text || localizedItem.text.trim() === '') {
      const anyLanguageText = textArray.find(t => t && t.text && t.text.trim() !== '');
      return anyLanguageText?.text || defaultText;
    }

    return localizedItem.text;
  };

  /**
   * Check if user guide should be displayed
   * 
   * @returns {boolean} Whether the user guide should be shown
   */
  const shouldShowUserGuide = () => {
    return form?.showUserGuide && form?.userGuideFiles?.[0];
  };

  /**
   * Check if user guide should be displayed and is not collapsed
   * 
   * @returns {boolean} Whether the user guide should be visible
   */
  const shouldShowUserGuideVisible = () => {
    return shouldShowUserGuide() && !isGuideCollapsed;
  };

  /**
   * Toggle user guide collapse state and persist user preference
   */
  const toggleGuideCollapse = () => {
    const newCollapsedState = !isGuideCollapsed;
    setIsGuideCollapsed(newCollapsedState);
    // Save user preference to localStorage
    localStorage.setItem('userGuideCollapsed', newCollapsedState.toString());
  };

  /**
   * Toggle context data panel collapse state and persist user preference
   */
  const toggleContextCollapse = () => {
    setIsContextCollapsed(prev => !prev);
  };

  /**
   * Check if context data should be displayed
   *
   * @returns {boolean} Whether the context data panel should be shown
   */
  const shouldShowContextData = () => {
    return formCompletion?.ContextData && formCompletion?.ContextData?.source;
  };

  /**
   * Get All Questions Helper Function
   * 
   * Flattens the form structure to get all questions in a single array
   * Used for visibility condition evaluation across the entire form
   * 
   * @returns {Array} Flat array of all questions from all sections
   */
  const getAllQuestions = () => {
    const allQuestions = [];
    if (form?.sections) {
      form.sections.forEach(section => {
        if (section.questions) {
          allQuestions.push(...section.questions);
        }
      });
    }
    return allQuestions;
  };

  const allQuestions = getAllQuestions();

  /**
   * Progress Bar Component
   * 
   * Renders a progress bar showing completion percentage of visible questions
   * 
   * @returns {JSX.Element} Progress bar component
   */
  const renderProgressBar = () => {
    // Don't show progress bar if no questions
    if (visibleQuestionsCount === 0) return null;

    // Check form-level settings for progress bars
    // Default to true only if the property is undefined, but respect explicit false
    const shouldShowFirstProgressBar = form?.showFirstProgressBar !== false;
    const shouldShowSecondProgressBar = form?.showSecondProgressBar !== false;

    // If neither progress bar should be shown, return null
    if (!shouldShowFirstProgressBar && !shouldShowSecondProgressBar) return null;

    // Get visible sections for second progress bar calculation
    const visibleSections = form?.sections?.filter(section => isSectionVisible(section)) || [];

    const progressBars = [];

    // First progress bar - Questions completion
    if (shouldShowFirstProgressBar) {
      progressBars.push(
        <div className="card mb-3" key="first-progress">
          <div className="card-body py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">
                <i className="fa-solid fa-chart-line me-2"></i>
                {t('progressCompletion', { ns: 'labels', defaultValue: 'Progress' })}
              </h6>
              <span className="badge" style={{
                backgroundColor: progressPercentage === 100 ? colors?.success || '#28a745' : colors?.primary || '#f05f23',
                color: colors?.white || '#fff'
              }}>
                {progressPercentage}%
              </span>
            </div>

            <div className="progress mb-2" style={{ height: '8px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{
                  width: `${progressPercentage}%`,
                  backgroundColor: progressPercentage === 100 ? colors?.success || '#28a745' : colors?.primary || '#f05f23'
                }}
                aria-valuenow={progressPercentage}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {t('questionsCompleted', { ns: 'labels', defaultValue: 'Questions completed' })}
              </small>
              <small className="text-muted">
                {answeredQuestionsCount}/{visibleQuestionsCount} {t('questions', { ns: 'labels', defaultValue: 'questions' })}
              </small>
            </div>
          </div>
        </div>
      );
    }

    // Second progress bar - Conformity score
    if (shouldShowSecondProgressBar) {
      // Get the number of scored questions for display purposes
      const visibleQuestions = getVisibleQuestions();
      const excludedTypes = ['table', 'period', 'datetime', 'date', 'time', 'vocabulary', 'customVocabulary', 'departmentFunctionalArea'];
      const scoredQuestions = visibleQuestions.filter(question =>
        !excludedTypes.includes(question.questionType) &&
        question.answers && Array.isArray(question.answers) && question.answers.length > 0
      );

      // Determine color based on conformity score ranges
      let conformityColor = colors?.danger || '#dc3545'; // Red for low scores (0-49)
      if (conformityScore >= 80) {
        conformityColor = colors?.success || '#28a745'; // Green for high scores (80-100)
      } else if (conformityScore >= 60) {
        conformityColor = colors?.warning || '#ffc107'; // Yellow for medium scores (60-79)
      } else if (conformityScore >= 50) {
        conformityColor = colors?.info || '#17a2b8'; // Blue for fair scores (50-59)
      }

      progressBars.push(
        <div className="card mb-3" key="second-progress">
          <div className="card-body py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">
                <i className="fa-solid fa-chart-bar me-2"></i>
                {t('conformityScore', { ns: 'labels', defaultValue: 'Conformity Score' })}
              </h6>
              <span className="badge" style={{
                backgroundColor: conformityColor,
                color: colors?.white || '#fff'
              }}>
                {conformityScore}%
              </span>
            </div>

            <div className="progress mb-2" style={{ height: '8px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{
                  width: `${conformityScore}%`,
                  backgroundColor: conformityColor
                }}
                aria-valuenow={conformityScore}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {t('scoreBasedOnAnswers', { ns: 'labels', defaultValue: 'Score based on answers' })}
              </small>
              <small className="text-muted">
                {scoredQuestions.length} {t('scoredQuestions', { ns: 'labels', defaultValue: 'scored questions' })}
              </small>
            </div>

            {/* Additional conformity level indicator */}
            <div className="mt-2">
              <small className="text-muted">
                {conformityScore >= 80 && t('excellentConformity', { ns: 'labels', defaultValue: 'Excellent conformity' })}
                {conformityScore >= 60 && conformityScore < 80 && t('goodConformity', { ns: 'labels', defaultValue: 'Good conformity' })}
                {conformityScore >= 50 && conformityScore < 60 && t('fairConformity', { ns: 'labels', defaultValue: 'Fair conformity' })}
              </small>
            </div>
          </div>
        </div>
      );
    }

    return <>{progressBars}</>;
  };

  /**
   * Initialize Default Values Function
   * 
   * Sets default values for questions that have defaultAnswers defined
   * This function is called after form data is loaded to apply retrocompatible defaults
   */
  const initializeDefaultValues = () => {
    if (!form?.sections) return;

    const defaultValues = {};

    // Process all sections and questions
    form.sections.forEach(section => {
      if (section.questions) {
        section.questions.forEach(question => {
          // Check if question has default answers and the types that support them
          if (question.defaultAnswers && Array.isArray(question.defaultAnswers) && question.defaultAnswers.length > 0) {
            const questionId = question.id || question.questionCode;
            const questionType = question.questionType;

            // Only set defaults for supported question types
            if (['single', 'multiple', 'boolean', 'yesNoNA', 'pdca', 'trueFalse'].includes(questionType)) {
              // Check if the question value is not already set (don't override existing values)
              if (formValues[questionId] === undefined || formValues[questionId] === null) {
                if (questionType === 'single' || questionType === 'boolean' || questionType === 'yesNoNA' || questionType === 'trueFalse') {
                  // Single selection questions: use the first default answer
                  defaultValues[questionId] = question.defaultAnswers[0];
                } else if (questionType === 'multiple' || questionType === 'pdca') {
                  // Multiple selection questions: use all default answers as an array
                  defaultValues[questionId] = [...question.defaultAnswers];
                }
              }
            }
          }
        });
      }
    });

    // Apply default values if any were found
    if (Object.keys(defaultValues).length > 0) {
      setFormValues(prev => ({
        ...prev,
        ...defaultValues
      }));
    }
  };

  /**
   * Initialize Table Defaults Function
   * 
   * Ensures that table questions always have at least one empty row
   * This function is called after form data is loaded and should only add empty rows
   * when there are no existing data for the table questions
   */
  const initializeTableDefaults = () => {
    if (!form?.sections) return;

    const tableDefaults = {};

    // Process all sections and questions to find table questions
    form.sections.forEach(section => {
      if (section.questions) {
        section.questions.forEach(question => {
          if (question.questionType === 'table') {
            const questionId = question.id || question.questionCode;

            // Check if the table question doesn't have any existing value or has an empty array
            const currentValue = formValues[questionId];

            // Only initialize if:
            // 1. No value exists (undefined/null) - new compilation
            // 2. Value exists but is empty array - compilation exists but table is empty
            // 3. Value exists but array has no meaningful data
            const shouldInitialize = !currentValue ||
              (Array.isArray(currentValue) && currentValue.length === 0) ||
              (Array.isArray(currentValue) && currentValue.every(row =>
                !row || Object.values(row).every(cell => !cell || String(cell).trim() === '')
              ));

            if (shouldInitialize) {
              // Create a default empty row based on table columns
              const emptyRow = {};

              // Use tableColumns from question structure (not columns)
              if (question.tableColumns && Array.isArray(question.tableColumns)) {
                question.tableColumns.forEach(column => {
                  const columnId = column.id || column.columnId || column.name;
                  emptyRow[columnId] = '';
                });
              } else if (question.columns && Array.isArray(question.columns)) {
                // Fallback to columns if tableColumns doesn't exist
                question.columns.forEach(column => {
                  const columnId = column.id || column.columnId || column.name;
                  emptyRow[columnId] = '';
                });
              }

              // Only set if we have at least one column defined
              if (Object.keys(emptyRow).length > 0) {
                tableDefaults[questionId] = [emptyRow];
              }
            }
          }
        });
      }
    });

    // Apply table defaults if any were found
    if (Object.keys(tableDefaults).length > 0) {
      setFormValues(prev => ({
        ...prev,
        ...tableDefaults
      }));
    }
  };

  /**
   * Initialize Default Values Effect
   * 
   * Runs after form data is loaded to apply default values
   * Only runs once per form load and doesn't override existing values
   */
  React.useEffect(() => {
    if (!form || loading || departmentsVoc.length !== 1) return;
    const onlyDeptId = departmentsVoc[0].Id;

    const autoFillValues = {};
    form.sections?.forEach(section => {
      section.questions?.forEach(question => {
        if (question.questionType === 'departmentFunctionalArea') {
          const qId = question.id || question.questionCode;
          const current = formValues[qId];
          // Solo se non c'è già un valore con dipartimento
          if (!current || !current.includes('|') || current.split('|')[0] === '') {
            autoFillValues[qId] = `${onlyDeptId}|`;
          }
        }
      });
    });

    if (Object.keys(autoFillValues).length > 0) {
      setFormValues(prev => ({ ...prev, ...autoFillValues }));
    }
  }, [form, loading, departmentsVoc]);

  /**
   * Add CSS to make question text inline with asterisk
   * This ensures the mandatory asterisk appears on the same line as the question text
   */
  React.useEffect(() => {
    const styleId = 'question-text-inline-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .question-text-inline .editorjs-content,
        .question-text-inline .editorjs-content > div,
        .question-text-inline .editorjs-content p {
          display: inline !important;
          margin: 0 !important;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  /**
   * Condition Evaluation Function
   *
   * Recursively evaluates visibility conditions for questions and sections
   * Supports CONDITION, AND, and OR operators with nested structures
   *
   * @param {Object} condition - The condition object to evaluate
   * @returns {boolean} Whether the condition is satisfied
   */
  const evaluateCondition = (condition) => {
    if (!condition) return true;

    // Base condition: compare question value with expected answer
    if (condition.type === "CONDITION") {
      const questionValue = formValues[condition.questionId];
      const expectedValue = condition.answerId;

      // Handle multiple choice questions (array values)
      if (Array.isArray(questionValue)) {
        if (condition.operator === "EQUALS") {
          return questionValue.includes(expectedValue);
        } else if (condition.operator === "NOT_EQUALS") {
          return !questionValue.includes(expectedValue);
        }
      }

      // Handle single choice questions (scalar values)
      if (condition.operator === "EQUALS") {
        return String(questionValue) === String(expectedValue);
      } else if (condition.operator === "NOT_EQUALS") {
        return String(questionValue) !== String(expectedValue);
      }

      return false;
    }

    // AND condition: all children must be true
    if (condition.type === "AND") {
      return condition.children.every(child => evaluateCondition(child));
    }

    // OR condition: at least one child must be true
    if (condition.type === "OR") {
      return condition.children.some(child => evaluateCondition(child));
    }

    return true;
  };

  /**
   * Question Visibility Check Function
   * 
   * Determines if a question should be displayed based on:
   * - Explicit invisible flag
   * - Visibility conditions based on other answers
   * 
   * @param {Object} question - The question object to check
   * @returns {boolean} Whether the question should be visible
   */
  const isQuestionVisible = (question) => {
    // If explicitly marked as invisible, don't show
    if (question.invisible) return false;

    // Check visibility conditions
    if (!question.visibilityCondition) return true;

    if (question.visibilityCondition.children && question.visibilityCondition.children.length < 1) return true;

    return evaluateCondition(question.visibilityCondition);
  };

  /**
   * Section Visibility Check Function
   * 
   * Determines if a section should be displayed based on:
   * - Explicit invisible flag
   * - Visibility conditions based on form answers
   * 
   * @param {Object} section - The section object to check
   * @returns {boolean} Whether the section should be visible
   */
  /**
   * Returns true if the section is assigned to the current user's actor(s),
   * either directly or via at least one individually assigned question,
   * OR if it's visible in read-only mode via canViewActors.
   */
  const isSectionAssignedOrViewable = (sectionId) => {
    if (!collaborativeFormsSchema || collaborativeFormsSchema.length === 0) return true;

    const isAssigned = collaborativeFormsSchema.some(actor => {
      if ((actor.sections || []).includes(sectionId)) return true;
      const sec = form?.sections?.find(s => s.id === sectionId);
      const secQIds = sec?.questions?.map(q => q.id) || [];
      return secQIds.some(qId => (actor.questions || []).includes(qId));
    });
    if (isAssigned) return true;

    return collaborativeFormsSchema.some(actor =>
      (actor.readOnlyActors || []).some(ro =>
        (ro.sections || []).some(s => (s?.id ?? s) === sectionId)
      )
    );
  };

  /**
   * Returns true if the section is visible only via canViewActors (read-only),
   * not directly assigned to the current user's actor.
   */
  const isSectionReadOnlyForUser = (sectionId) => {
    if (!isCollaborative || !collaborativeFormsSchema || collaborativeFormsSchema.length === 0) return false;

    const isAssigned = collaborativeFormsSchema.some(actor => {
      if ((actor.sections || []).includes(sectionId)) return true;
      const sec = form?.sections?.find(s => s.id === sectionId);
      const secQIds = sec?.questions?.map(q => q.id) || [];
      return secQIds.some(qId => (actor.questions || []).includes(qId));
    });
    if (isAssigned) return false;

    return collaborativeFormsSchema.some(actor =>
      (actor.readOnlyActors || []).some(ro =>
        (ro.sections || []).some(s => (s?.id ?? s) === sectionId)
      )
    );
  };

  const isSectionVisible = (section) => {
    // If explicitly marked as invisible, don't show
    if (section.invisible) return false;

    // Check visibility conditions
    if (section.visibilityCondition &&
      section.visibilityCondition.children &&
      section.visibilityCondition.children.length > 0 &&
      !evaluateCondition(section.visibilityCondition)) {
      return false;
    }

    // For collaborative forms, only show sections assigned to the current actor
    // or explicitly visible via canViewActors
    if (isCollaborative && collaborativeFormsSchema && collaborativeFormsSchema.length > 0) {
      return isSectionAssignedOrViewable(section.id);
    }

    return true;
  };

  /**
   * Get Visible Questions Helper Function
   * 
   * Returns all questions that are currently visible based on section and question visibility conditions
   * 
   * @returns {Array} Array of currently visible questions
   */
  const getVisibleQuestions = () => {
    const visibleQuestions = [];
    if (form?.sections) {
      form.sections.forEach(section => {
        // Only process sections that are visible
        if (isSectionVisible(section) && section.questions) {
          section.questions.forEach(question => {
            // Only include questions that are visible
            if (isQuestionVisible(question)) {
              visibleQuestions.push(question);
            }
          });
        }
      });
    }
    return visibleQuestions;
  };

  /**
   * Check if Question is Answered Helper Function
   * 
   * Determines if a question has been answered based on its type and current form values
   * 
   * @param {Object} question - The question object to check
   * @returns {boolean} Whether the question has been answered
   */
  const isQuestionAnswered = (question) => {
    const questionId = question.id || question.questionCode;
    const questionValue = formValues[questionId];
    const hasAttachments = (answerFiles[questionId]?.length > 0) || answerLinks[questionId] ||
      answerTexts[questionId] || answerObservations[questionId];

    // Check if question has a meaningful answer based on question type
    let hasAnswer = false;

    if (question.questionType === 'multiple' || question.questionType === 'pdca') {
      // Multiple choice: check if array has at least one selection
      hasAnswer = Array.isArray(questionValue) && questionValue.length > 0;
    } else if (['single', 'boolean', 'yesNoNA', 'trueFalse'].includes(question.questionType)) {
      // Single choice: check if a value is selected
      hasAnswer = questionValue !== undefined && questionValue !== null && questionValue !== '';
    } else if (question.questionType === 'departmentFunctionalArea') {
      // Department/Functional Area: check if BOTH department and functional area are selected
      if (questionValue && questionValue.includes('|')) {
        const [departmentId, functionalAreaId] = questionValue.split('|');
        hasAnswer = departmentId && departmentId.trim() !== '' && functionalAreaId && functionalAreaId.trim() !== '';
      } else {
        hasAnswer = false;
      }
    } else if (['vocabulary', 'customVocabulary'].includes(question.questionType)) {
      // Vocabulary choice: check if at least one value is selected (supports both single and multiple)
      if (Array.isArray(questionValue)) {
        hasAnswer = questionValue.length > 0;
      } else {
        hasAnswer = questionValue !== undefined && questionValue !== null && questionValue !== '';
      }
    } else if (question.questionType === 'open' || question.questionType === 'openNumber') {
      // Text input: check if text is provided
      hasAnswer = questionValue !== undefined && questionValue !== null &&
        String(questionValue).trim() !== '';
    } else if (['date', 'time', 'datetime'].includes(question.questionType)) {
      hasAnswer = questionValue !== undefined && questionValue !== null &&
        String(questionValue).trim() !== '';
    } else if (question.questionType === 'period') {
      // Period: check that at least one of start/end is provided; and if both provided, start <= end
      const raw = formValues[questionId];
      if (typeof raw === 'string' && raw.includes('|')) {
        const [s, e] = raw.split('|');
        const hasStart = s && String(s).trim() !== '';
        const hasEnd = e && String(e).trim() !== '';
        if (hasStart || hasEnd) {
          hasAnswer = true;
          if (hasStart && hasEnd) {
            try {
              hasAnswer = new Date(s).getTime() <= new Date(e).getTime();
            } catch {
              hasAnswer = false;
            }
          }
        } else {
          hasAnswer = false;
        }
      } else {
        hasAnswer = false;
      }
    } else if (question.questionType === 'table') {
      // Table: check if at least one row of data is provided
      if (Array.isArray(questionValue)) {
        hasAnswer = questionValue.length > 0 && questionValue.some(row => {
          // Check if at least one cell in the row has data
          return Object.entries(row).some(([colId, cellValue]) => {
            if (cellValue === undefined || cellValue === null || String(cellValue).trim() === '') return false;
            // For period type, ensure at least start or end present
            const columnDef = (question.tableColumns || []).find(c => c.id === colId);
            if (columnDef && columnDef.type === 'period') {
              const parts = String(cellValue).split('|');
              const hasStart = parts[0] && String(parts[0]).trim() !== '';
              const hasEnd = parts[1] && String(parts[1]).trim() !== '';
              return hasStart || hasEnd;
            }
            return true;
          });
        });
      } else {
        hasAnswer = false;
      }
    }

    // If no answer but has attachments, consider as answered
    return hasAnswer || hasAttachments;
  };

  /**
   * Calculate Question Score for Conformity
   * 
   * Calculates the score obtained by the user for a specific question
   * based on the selected answers and their scores
   * 
   * @param {Object} question - The question object
   * @returns {number} The total score obtained for this question
   */
  const calculateQuestionScore = (question) => {
    const questionId = question.id || question.questionCode;
    const questionValue = formValues[questionId];

    // If no answer provided, score is 0
    if (!questionValue) return 0;

    let obtainedScore = 0;

    if (question.questionType === 'single' || question.questionType === 'boolean' || question.questionType === 'trueFalse' || question.questionType === 'yesNoNA') {
      // Single selection: find the selected answer and get its score
      const selectedAnswer = question.answers?.find(a =>
        String(a.answerId || a.id) === String(questionValue)
      );
      // Exclude answers marked as not applicable from scoring
      if (selectedAnswer && !selectedAnswer.notApplicable) {
        obtainedScore = selectedAnswer.score || 0;
      }

    } else if (question.questionType === 'multiple' || question.questionType === 'pdca') {
      // Multiple selection: sum scores of all selected answers
      if (Array.isArray(questionValue)) {
        obtainedScore = questionValue.reduce((sum, answerId) => {
          const selectedAnswer = question.answers?.find(a =>
            String(a.answerId || a.id) === String(answerId)
          );
          // Exclude answers marked as not applicable from scoring
          if (selectedAnswer && !selectedAnswer.notApplicable) {
            return sum + (selectedAnswer.score || 0);
          }
          return sum;
        }, 0);
      }
    } else if (question.questionType === 'open' || question.questionType === 'openNumber') {
      // Open questions: check if they have predefined answers with scores
      if (question.answers && Array.isArray(question.answers) && question.answers.length > 0) {
        // If there are predefined answers, try to match the user's input
        const selectedAnswer = question.answers.find(a =>
          String(a.answerText?.[0]?.text || a.answerText || '').toLowerCase() === String(questionValue).toLowerCase()
        );
        // Exclude answers marked as not applicable from scoring
        if (selectedAnswer && !selectedAnswer.notApplicable) {
          obtainedScore = selectedAnswer.score || 0;
        }
      } else {
        // No predefined answers, return 0
        return 0;
      }
    } else {
      // For excluded question types (table, period, date, time, vocabulary, etc.), return 0
      return 0;
    }

    return obtainedScore;
  };

  /**
   * Calculate Maximum Possible Score for a Question
   * 
   * Calculates the maximum score that can be obtained for a question
   * based on its type and answer configuration
   * 
   * @param {Object} question - The question object
   * @returns {number} The maximum possible score for this question
   */
  const calculateQuestionMaxScore = (question) => {
    if (!question.answers || !Array.isArray(question.answers)) return 0;

    // Filter out invisible answers and not applicable answers
    const applicableAnswers = question.answers.filter(a => !a.invisible && !a.notApplicable);

    if (question.questionType === 'single' || question.questionType === 'boolean' || question.questionType === 'trueFalse' || question.questionType === 'yesNoNA') {
      // Single selection: maximum is the highest score among applicable answers
      if (applicableAnswers.length === 0) return 0;
      return Math.max(...applicableAnswers.map(a => a.score || 0));

    } else if (question.questionType === 'multiple' || question.questionType === 'pdca') {
      // Multiple selection: maximum is the sum of all applicable answer scores
      return applicableAnswers.reduce((sum, answer) => sum + (answer.score || 0), 0);

    } else if (question.questionType === 'open' || question.questionType === 'openNumber') {
      // Open questions: maximum is the highest score among applicable predefined answers (if any)
      if (applicableAnswers.length > 0) {
        return Math.max(...applicableAnswers.map(a => a.score || 0));
      }
      return 0;
    }

    // For excluded question types, return 0
    return 0;
  };

  /**
   * Calculate Conformity Score
   * 
   * Calculates the overall conformity percentage based on question scores.
   * This represents the level of conformity established by the sum of total scores 
   * of all responses given by the user DIVIDED by the total sum of scores assigned 
   * to the answers of each question.
   * 
   * The conformity score provides insight into how well the user's answers align
   * with the expected or optimal responses based on the scoring system defined
   * in the form.
   * 
   * INCLUDED question types: single, multiple, boolean/trueFalse, yesNoNA, pdca, open, openNumber
   * EXCLUDED question types: table, period, datetime, date, time, vocabulary, customVocabulary, departmentFunctionalArea
   * 
   * Formula: (Sum of obtained scores / Sum of maximum possible scores) * 100
   * 
   * Example:
   * - Question 1 (single): User selected answer with score 0.25, max possible is 0.25 → 100% for this question
   * - Question 2 (multiple): User selected answers with scores [0.33, 0.33], max possible is 0.99 → 66.7% for this question  
   * - Overall conformity: (0.25 + 0.66) / (0.25 + 0.99) * 100 = 73.4%
   * 
   * @returns {number} Conformity percentage (0-100)
   */
  const calculateConformityScore = () => {
    const visibleQuestions = getVisibleQuestions();

    // Exclude these question types from conformity calculation
    const excludedTypes = ['table', 'period', 'datetime', 'date', 'time', 'vocabulary', 'customVocabulary', 'departmentFunctionalArea'];

    // Filter questions that can have scored answers (all types except excluded ones)
    const scoredQuestions = visibleQuestions.filter(question =>
      !excludedTypes.includes(question.questionType) &&
      question.answers && Array.isArray(question.answers) && question.answers.length > 0
    );

    if (scoredQuestions.length === 0) return 0;

    let totalObtainedScore = 0;
    let totalMaxScore = 0;

    scoredQuestions.forEach(question => {
      const obtainedScore = calculateQuestionScore(question);
      const maxScore = calculateQuestionMaxScore(question);

      totalObtainedScore += obtainedScore;
      totalMaxScore += maxScore;
    });

    // Avoid division by zero
    if (totalMaxScore === 0) return 0;

    return Math.round((totalObtainedScore / totalMaxScore) * 100);
  };

  /**
   * Calculate Progress Percentage with useMemo
   * 
   * Calculates the completion percentage based on visible questions
   * Uses useMemo to recalculate only when dependencies change
   */
  const { progressPercentage, visibleQuestionsCount, answeredQuestionsCount, conformityScore } = React.useMemo(() => {
    const visibleQuestions = getVisibleQuestions();
    if (visibleQuestions.length === 0) {
      return {
        progressPercentage: 100,
        visibleQuestionsCount: 0,
        answeredQuestionsCount: 0,
        conformityScore: 0
      };
    }

    const answeredQuestions = visibleQuestions.filter(question => isQuestionAnswered(question));
    const percentage = Math.round((answeredQuestions.length / visibleQuestions.length) * 100);
    const conformity = calculateConformityScore();

    return {
      progressPercentage: percentage,
      visibleQuestionsCount: visibleQuestions.length,
      answeredQuestionsCount: answeredQuestions.length,
      conformityScore: conformity
    };
  }, [form, formValues, answerFiles, answerLinks, answerTexts, answerObservations]);

  /**
   * Input Change Handler Function
   * 
   * Updates form values state when user inputs change
   * Prevents changes in read-only mode and sets dirty flag for compilation mode
   * Also performs real-time validation for multiple choice questions
   * 
   * @param {string} questionId - ID of the question being updated
   * @param {any} value - New value for the question
   */
  const handleInputChange = (questionId, value) => {
    // Prevent changes in read-only mode
    if (effectiveReadOnly) return;

    setFormValues(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Set dirty flag to warn about unsaved changes (compilation mode only)
    if (!isSimulation) {
      setPreventQuit(true);
    }

    // Perform validation for multiple choice questions
    const question = getAllQuestions().find(q => (q.id || q.questionCode) === questionId);
    if (question && (question.questionType === 'multiple' || question.questionType === 'pdca')) {
      const userAnswerIds = Array.isArray(value) ? value : [];
      const validationResult = validateQuestion(question, userAnswerIds);

      setValidationErrors(prev => ({
        ...prev,
        [questionId]: validationResult.errors
      }));
    } else {
      // Clear validation errors for non-multiple questions
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  /**
   * Image File Type Check Function
   * 
   * Determines if a file is an image based on its MIME type
   * Used to decide whether to show image preview or file download link
   * 
   * @param {string} mimeType - MIME type of the file
   * @returns {boolean} Whether the file is an image
   */
  const isImageFile = (mimeType) => {
    return mimeType && mimeType.startsWith('image/');
  };

  /**
   * File URL Generator Function
   * 
   * Generates download URL for files in the document management system
   * Returns both URL and MIME type for proper handling
   * 
   * @param {Object} file - File object with ID and metadata
   * @returns {Object} Object containing url and mimeType properties
   */
  const getUrlFromFile = (file) => {
    // In simulation mode, we don't need a real URL as we won't be fetching
    if (isSimulation) {
      return {
        url: '#simulation',
        mimeType: file.MimeType || 'application/octet-stream'
      };
    }

    // Get the complete user object
    const user = currentUser();

    // Serialize the user object to pass it as query parameter
    const userParam = encodeURIComponent(JSON.stringify(user));

    // Construct file download URL using the DMS API endpoint with user object
    const fileUrl = `${process.env.REACT_APP_API_URL}/dms/downloadFile/get/${file.Id}?user=${userParam}`;

    return {
      url: fileUrl,
      mimeType: file.MimeType || 'application/octet-stream'
    };
  };

  /**
   * Authenticated File Download Function
   * 
   * Downloads files using the existing downloadFileGet API function
   * This ensures proper authentication and error handling
   * 
   * @param {Object} file - File object with ID, Name, and metadata
   */
  const handleFileDownload = async (file) => {
    if (isSimulation) {
      MySwal.fire({
        title: t('simulationMode', { ns: 'labels', defaultValue: 'Simulation Mode' }),
        text: t('downloadDisabledInSim', { ns: 'labels', defaultValue: 'File downloads are disabled in simulation mode.' }),
        icon: 'info',
        confirmButtonColor: colors?.info || '#17a2b8'
      });
      return;
    }
    try {
      // Show loading indicator
      MySwal.fire({
        title: t('downloading', { ns: 'labels', defaultValue: 'Downloading...' }),
        text: file.Name || 'File',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Use the existing authenticated download API function
      const blob = await api.downloadFileGet(`dms/downloadFile/get/${file.Id}`, {
        user: currentUser()
      });

      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.Name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      window.URL.revokeObjectURL(downloadUrl);

      // Close loading indicator
      MySwal.close();

    } catch (error) {
      console.error('Download error:', error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: t('downloadFailed', { ns: 'labels', defaultValue: 'Failed to download file' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    }
  };

  /**
   * Open/View File Function
   * 
   * Opens files in a new window/tab for viewing when possible
   * Falls back to download for files that cannot be displayed in browser
   * 
   * @param {Object} file - File object with ID, Name, and metadata
   */
  const handleFileOpen = async (file) => {
    if (isSimulation) {
      MySwal.fire({
        title: t('simulationMode', { ns: 'labels', defaultValue: 'Simulation Mode' }),
        text: t('fileViewDisabledInSim', { ns: 'labels', defaultValue: 'File viewing is disabled in simulation mode.' }),
        icon: 'info',
        confirmButtonColor: colors?.info || '#17a2b8'
      });
      return;
    }

    try {
      // Show loading indicator
      MySwal.fire({
        title: t('opening', { ns: 'labels', defaultValue: 'Opening...' }),
        text: file.Name || 'File',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Use the existing authenticated download API function to get the blob
      const blob = await api.downloadFileGet(`dms/downloadFile/get/${file.Id}`, {
        user: currentUser()
      });

      // Create object URL for viewing
      const fileUrl = window.URL.createObjectURL(blob);
      const mimeType = file.MimeType || 'application/octet-stream';

      // Close loading indicator
      MySwal.close();

      // Check if file can be displayed in browser
      const viewableTypes = [
        'application/pdf',
        'text/plain',
        'text/html',
        'text/css',
        'text/javascript',
        'application/json',
        'text/csv',
        'application/xml',
        'text/xml'
      ];

      const isViewable = viewableTypes.includes(mimeType) ||
        mimeType.startsWith('image/') ||
        mimeType.startsWith('video/') ||
        mimeType.startsWith('audio/');

      if (isViewable) {
        // Open in new tab for viewing
        const newWindow = window.open(fileUrl, '_blank');
        if (!newWindow) {
          // Popup blocked, show message
          MySwal.fire({
            title: t('popupBlocked', { ns: 'labels', defaultValue: 'Popup Blocked' }),
            text: t('enablePopupsToView', { ns: 'labels', defaultValue: 'Please enable popups to view files in new window.' }),
            icon: 'warning',
            confirmButtonColor: colors?.warning || '#ffc107'
          });
        }

        // Clean up the object URL after a delay to allow the browser to load it
        setTimeout(() => {
          window.URL.revokeObjectURL(fileUrl);
        }, 10000);
      } else {
        // File type not viewable, offer download instead
        MySwal.fire({
          title: t('fileNotViewable', { ns: 'labels', defaultValue: 'File Not Viewable' }),
          text: t('fileNotViewableMessage', { ns: 'labels', defaultValue: 'This file type cannot be viewed in the browser. Would you like to download it instead?' }),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: colors?.primary || '#f05f23',
          cancelButtonColor: colors?.secondary || '#6c757d',
          confirmButtonText: t('download', { ns: 'labels', defaultValue: 'Download' }),
          cancelButtonText: t('cancel', { ns: 'labels', defaultValue: 'Cancel' })
        }).then((result) => {
          if (result.isConfirmed) {
            // Trigger download
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = file.Name || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          // Clean up the object URL
          window.URL.revokeObjectURL(fileUrl);
        });
      }

    } catch (error) {
      console.error('File open error:', error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: t('fileOpenFailed', { ns: 'labels', defaultValue: 'Failed to open file' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    }
  };

  /**
   * Authenticated Image Loader Function
   * 
   * Loads images using authenticated API request and creates blob URLs
   * This allows images to be displayed in <img> elements with proper authentication
   * 
   * @param {Object} file - File object with ID, Name, and metadata
   */
  const loadAuthenticatedImage = async (file) => {
    // Do not attempt to load images in simulation mode
    if (isSimulation) {
      return null;
    }
    // Check if image is already loaded
    if (loadedImages[file.Id]) {
      return loadedImages[file.Id];
    }

    try {
      // Use the existing authenticated download API function
      const blob = await api.downloadFileGet(`dms/downloadFile/get/${file.Id}`, {
        user: currentUser()
      });

      // Create blob URL for image
      const imageUrl = window.URL.createObjectURL(blob);

      // Store in state to avoid reloading
      setLoadedImages(prev => ({
        ...prev,
        [file.Id]: imageUrl
      }));

      return imageUrl;

    } catch (error) {
      console.error('Image loading error:', error);
      return null; // Return null on error, will fallback to placeholder
    }
  };

  /**
   * Image Component with Authentication
   * 
   * Renders an image with authenticated loading
   * 
   * @param {Object} props - Component props
   * @param {Object} props.file - File object
   * @param {string} props.alt - Alt text for image
   * @param {string} props.className - CSS classes
   * @param {Object} props.style - Inline styles
   * @param {Function} props.onClick - Click handler
   */
  const AuthenticatedImage = ({ file, alt, className, style, onClick }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
      loadAuthenticatedImage(file)
        .then(url => {
          setImageUrl(url);
          setLoading(false);
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    }, [file.Id]);

    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ ...style, minHeight: '100px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading image...</span>
          </div>
        </div>
      );
    }

    if (error || !imageUrl) {
      return (
        <div className="d-flex justify-content-center align-items-center bg-light" style={{ ...style, minHeight: '100px' }}>
          <div className="text-muted">
            <i className="fa-solid fa-image me-2"></i>
            {t('imageLoadError', { ns: 'labels', defaultValue: 'Unable to load image' })}
          </div>
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
      />
    );
  };


  /**
   * Score Formatting Function
   * 
   * Formats numeric scores for display, removing unnecessary decimal places
   * Handles null/undefined values and ensures clean number presentation
   * 
   * @param {number|string} score - The score value to format
   * @returns {string|null} Formatted score string or null if invalid
   */
  const formatScore = (score) => {
    if (score === null || score === undefined) return null;

    // Parse and validate the score
    const numScore = parseFloat(score);
    if (isNaN(numScore)) return null;

    // Show whole numbers without decimals
    if (numScore % 1 === 0) {
      return numScore.toString();
    }

    // Show decimals up to 2 places, removing trailing zeros
    return numScore.toFixed(2).replace(/\.?0+$/, '');
  };

  /**
   * Format Conformity Score for Display
   * 
   * Formats conformity score with appropriate styling and text based on score ranges
   * Provides visual feedback about the conformity level
   * 
   * @param {number} score - The conformity score (0-100)
   * @returns {Object} Object with formatted text, color, and level information
   */
  const formatConformityScore = (score) => {
    if (typeof score !== 'number' || isNaN(score)) {
      return {
        text: '0%',
        color: colors?.secondary || '#6c757d',
        level: 'unknown'
      };
    }

    let level = 'low';
    let color = colors?.danger || '#dc3545';

    if (score >= 80) {
      level = 'excellent';
      color = colors?.success || '#28a745';
    } else if (score >= 60) {
      level = 'good';
      color = colors?.warning || '#ffc107';
    } else if (score >= 50) {
      level = 'fair';
      color = colors?.info || '#17a2b8';
    }

    return {
      text: `${Math.round(score)}%`,
      color: color,
      level: level
    };
  };

  /**
   * Mandatory Validation Function
   * 
   * Validates that all mandatory sections and questions have been completed
   * Returns validation results with detailed error information for user feedback
   * 
   * @returns {Object} Validation result with isValid flag and detailed errors
   */
  const validateMandatorySections = () => {
    if (!form?.sections) return { isValid: true };

    const validationErrors = [];

    // Check each section for mandatory requirements
    for (const section of form.sections) {
      // Skip sections that are not visible due to conditions
      if (!isSectionVisible(section)) continue;

      // Check if this section has any mandatory requirements
      const hasMandatoryRequirements = section.mandatory ||
        (section.questions && section.questions.some(q => q.mandatory && isQuestionVisible(q)));

      if (hasMandatoryRequirements) {
        const sectionName = getLocalizedText(section.name, `Section ${form.sections.indexOf(section) + 1}`);
        const incompleteMandatoryQuestions = [];

        if (section.questions) {
          for (const question of section.questions) {
            // Skip questions that are not visible due to conditions
            if (!isQuestionVisible(question)) continue;

            // In mandatory sections, ALL visible questions must be answered
            // In non-mandatory sections, only questions marked as mandatory must be answered
            const questionMustBeAnswered = section.mandatory || question.mandatory;

            if (questionMustBeAnswered) {
              const questionId = question.id || question.questionCode;
              const questionValue = formValues[questionId];
              const hasAttachments = (answerFiles[questionId]?.length > 0) || answerLinks[questionId] ||
                answerTexts[questionId] || answerObservations[questionId];

              // Check if question has a meaningful answer based on question type
              let hasAnswer = false;

              if (question.questionType === 'multiple' || question.questionType === 'pdca') {
                // Multiple choice: check if array has at least one selection
                hasAnswer = Array.isArray(questionValue) && questionValue.length > 0;
              } else if (['single', 'boolean', 'yesNoNA', 'trueFalse'].includes(question.questionType)) {
                // Single choice: check if a value is selected
                hasAnswer = questionValue !== undefined && questionValue !== null && questionValue !== '';
              } else if (question.questionType === 'departmentFunctionalArea') {
                // Department/Functional Area: check if BOTH department and functional area are selected
                if (questionValue && questionValue.includes('|')) {
                  const [departmentId, functionalAreaId] = questionValue.split('|');
                  hasAnswer = departmentId && departmentId.trim() !== '' && functionalAreaId && functionalAreaId.trim() !== '';
                } else {
                  hasAnswer = false;
                }
              } else if (['vocabulary', 'customVocabulary'].includes(question.questionType)) {
                // Vocabulary choice: check if at least one value is selected (supports both single and multiple)
                if (Array.isArray(questionValue)) {
                  hasAnswer = questionValue.length > 0;
                } else {
                  hasAnswer = questionValue !== undefined && questionValue !== null && questionValue !== '';
                }
              } else if (question.questionType === 'open' || question.questionType === 'openNumber') {
                // Text input: check if text is provided
                hasAnswer = questionValue !== undefined && questionValue !== null &&
                  String(questionValue).trim() !== '';
              } else if (['date', 'time', 'datetime'].includes(question.questionType)) {
                hasAnswer = questionValue !== undefined && questionValue !== null &&
                  String(questionValue).trim() !== '';
              } else if (question.questionType === 'table') {
                // Table: check if at least one row of data is provided
                if (Array.isArray(questionValue)) {
                  hasAnswer = questionValue.length > 0 && questionValue.some(row => {
                    // Check if at least one cell in the row has data
                    return Object.values(row).some(cellValue =>
                      cellValue !== undefined && cellValue !== null && String(cellValue).trim() !== ''
                    );
                  });
                } else {
                  hasAnswer = false;
                }
              }

              // If no answer and no attachments, mark as incomplete
              if (!hasAnswer && !hasAttachments) {
                let questionText = getLocalizedText(question.questionText, `Question ${section.questions.indexOf(question) + 1}`);

                // Extract text from EditorJS format if needed
                try {
                  const parsedContent = JSON.parse(questionText);
                  if (parsedContent.blocks && Array.isArray(parsedContent.blocks)) {
                    // Extract text from all blocks
                    questionText = parsedContent.blocks
                      .map(block => block.data?.text || '')
                      .filter(text => text.trim() !== '')
                      .join(' ');
                  }
                } catch (e) {
                  // Not JSON or parsing failed, use as is
                }

                incompleteMandatoryQuestions.push(questionText);
              }
            }
          }
        }

        // Add section to errors if it has incomplete questions
        if (incompleteMandatoryQuestions.length > 0) {
          validationErrors.push({
            sectionName,
            incompleteMandatoryQuestions,
            isIndividualQuestion: !section.mandatory // Mark individual questions vs section-wide requirement
          });
        }
      }
    }

    return {
      isValid: validationErrors.length === 0,
      errors: validationErrors
    };
  };

  /**
   * Form Completion Save Function (Compilation Mode Only)
   * 
   * Saves the current form state to the backend without marking as completed
   * Allows users to save progress and return later to finish the form
   * Handles both creation of new completions and updates to existing ones
   */
  const saveFormCompletion = async () => {
    if (isSimulation) return; // Not needed in simulation mode
    if (effectiveReadOnly) return; // Don't allow saving in read-only mode

    try {
      // Show loading indicator during save operation
      MySwal.fire({
        title: t('saving', { ns: 'labels', defaultValue: 'Saving...' }),
        text: t('savingFormData', { ns: 'labels', defaultValue: 'Saving your form data' }),
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Collect answers from all questions
      const answersData = [];

      // Process all questions in the form
      const allQuestions = getAllQuestions();

      for (const question of allQuestions) {
        const questionId = question.id || question.questionCode;
        const questionValue = formValues[questionId];

        // Skip questions with no answers, not visible, or read-only for current actor
        // Special handling for date/time questions which have default values
        // Also keep questions that have attachments even if they have no value
        const hasQuestionAttachments = (answerFiles[questionId]?.length > 0) ||
          answerLinks[questionId] || answerTexts[questionId] || answerObservations[questionId];
        const shouldSkip = !isQuestionVisible(question) ||
          isQuestionReadOnlyCollaborative(questionId) ||
          (questionValue === undefined || questionValue === null) &&
          !['date', 'time', 'datetime'].includes(question.questionType) &&
          !hasQuestionAttachments;
        if (shouldSkip) continue;

        // Get localized question text
        const questionText = getLocalizedText(question.questionText, '');

        // Create standardized response object following backend data structure
        const questionResponse = {
          // Required fields (always present)
          questionId: questionId,
          questionType: question.questionType || '',
          questionText: questionText,

          // Answers array (always present, can be empty)
          answers: [],

          // Optional attachments (always present as fields, null if not used)
          files: (answerFiles[questionId] || []).map(f => ({ Id: f.Id, Name: f.Name, MimeType: f.MimeType, Size: f.Size })),
          file: answerFiles[questionId]?.[0]?.Id || null,
          fileName: answerFiles[questionId]?.[0]?.Name || null,
          fileMimeType: answerFiles[questionId]?.[0]?.MimeType || null,
          fileSize: answerFiles[questionId]?.[0]?.Size || null,

          link: answerLinks[questionId] || null,
          text: answerTexts[questionId] || null,
          observation: answerObservations[questionId] || null
        };

        // Handle answers based on question type following standardized structure
        if (question.questionType === 'vocabulary' && questionValue) {
          // GROUP 3 - VOCABULARY (supports multiple selection)
          const selectedValues = Array.isArray(questionValue) ? questionValue : [questionValue];

          selectedValues.forEach(value => {
            const vocItem = vocabularyItems[question.selectedVocabulary]?.find(v => v.Code === value);

            if (vocItem) {
              questionResponse.answers.push({
                answerId: vocItem.Code,
                answerText: vocItem.Name,
                answerType: 'vocabulary'
              });
            }
          });

          // For backward compatibility, set vocName to the first selected value
          if (selectedValues.length > 0) {
            const firstVocItem = vocabularyItems[question.selectedVocabulary]?.find(v => v.Code === selectedValues[0]);
            if (firstVocItem) {
              questionResponse.vocName = firstVocItem.Code;
            }
          }
        } else if (question.questionType === 'customVocabulary' && questionValue) {
          // GROUP 3 - CUSTOM VOCABULARY (supports multiple selection)
          const selectedValues = Array.isArray(questionValue) ? questionValue : [questionValue];

          for (let i = 0; i < selectedValues.length; i++) {
            const value = selectedValues[i];
            const customVocItem = customVocabularyItems[question.selectedCustomVocabulary]?.find(v => v.Code === value);

            const guid = await getGuid();
            questionResponse.answers.push({
              answerId: guid || '',
              vocValueText: customVocItem?.Name || value,
              answerType: 'customVocabulary',
              vocValueId: customVocItem?.Id || customVocItem?.Code || value
            });
          }

          // Add voc object with vocabulary parent information (for backward compatibility, use first selected)
          if (selectedValues.length > 0) {
            const firstCustomVocItem = customVocabularyItems[question.selectedCustomVocabulary]?.find(v => v.Code === selectedValues[0]);
            questionResponse.voc = {
              id: question.selectedCustomVocabulary,
              name: firstCustomVocItem?.Code || selectedValues[0]
            };
          }
        } else if ((question.questionType === 'multiple' || question.questionType === 'pdca') && Array.isArray(questionValue)) {
          // GROUP 5 - MULTIPLE CHOICE (including PDCA)
          questionValue.forEach(answerId => {
            const selectedAnswer = question.answers.find(a =>
              String(a.answerId || a.id) === String(answerId)
            );

            if (selectedAnswer) {
              questionResponse.answers.push({
                answerId: answerId,
                answerText: getLocalizedText(selectedAnswer.answerText, ''),
                answerType: 'predefined'
              });
            }
          });
        } else if (['single', 'boolean', 'yesNoNA', 'trueFalse'].includes(question.questionType)) {
          // GROUP 1 - SINGLE CHOICE
          const selectedAnswer = question.answers.find(a =>
            String(a.answerId || a.id) === String(questionValue)
          );

          if (selectedAnswer) {
            questionResponse.answers.push({
              answerId: questionValue,
              answerText: getLocalizedText(selectedAnswer.answerText, ''),
              answerType: 'predefined'
            });
          }
        } else if (question.questionType === 'open' || question.questionType === 'openNumber') {
          // GROUP 2 - FREE INPUT - Generate GUID for answerId
          const guid = await getGuid();
          questionResponse.answers.push({
            answerId: guid || '',  // Generated GUID or empty if failed
            answerText: questionValue,
            answerType: 'userInput'
          });
        } else if (['date', 'time', 'datetime'].includes(question.questionType)) {
          // GROUP 4 - DATE/TIME - Generate GUID for answerId
          const guid = await getGuid();

          // Use default value if questionValue is undefined
          let answerText = questionValue;
          /* if (answerText === undefined || answerText === null) {
            // Apply same default values as in the input fields
            if (question.questionType === 'date') {
              answerText = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
            } else if (question.questionType === 'time') {
              answerText = new Date().toTimeString().split(' ')[0].substr(0, 5); // HH:MM
            } else if (question.questionType === 'datetime') {
              answerText = new Date().toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
            }
          } */

          questionResponse.answers.push({
            answerId: guid || '',  // Generated GUID or empty if failed
            answerText: answerText,
            answerType: 'userInput'
          });
        } else if (question.questionType === 'period') {
          // GROUP 8 - PERIOD - Generate GUID and store start|end
          const guid = await getGuid();
          const raw = formValues[questionId] || '';
          questionResponse.answers.push({
            answerId: guid || '',
            answerText: String(raw),
            answerType: 'period'
          });
        } else if (question.questionType === 'departmentFunctionalArea' && questionValue) {
          // GROUP 6 - DEPARTMENT/FUNCTIONAL AREA - Generate GUID for answerId
          const guid = await getGuid();

          questionResponse.answers.push({
            answerId: guid || '',  // Generated GUID or empty if failed
            answerText: questionValue, // Combined value: "departmentId|functionalAreaId"
            answerType: 'userInput'
          });
        } else if (question.questionType === 'table' && Array.isArray(questionValue)) {
          // GROUP 7 - TABLE - Generate GUID for answerId
          const guid = await getGuid();

          questionResponse.answers.push({
            answerId: guid || '',  // Generated GUID or empty if failed
            answerText: JSON.stringify(questionValue), // Serialize table data as JSON
            answerType: 'tableData'
          });
        }

        // Add to answers array only if there are meaningful contents
        if (questionResponse.answers.length > 0 ||
          questionResponse.file !== null ||
          questionResponse.link !== null ||
          questionResponse.text !== null ||
          questionResponse.observation !== null) {
          answersData.push(questionResponse);
        }
      }

      // Create or update form completion with proper data structure
      const compilationData = {
        ...formCompletion,
        data: {
          // Preserve existing data structure from backend
          ...(formCompletion?.data || {}),
          answers: answersData,
          // Update timestamp information
          lastSaved: new Date().toISOString()
        }
      };

      // Send save request to backend
      const result = await api.sendMessage("darm/formCompletion/save", {
        user: currentUser(),
        body: {
          formCompletion: compilationData
        }
      });

      if (result?.success) {
        // Update completion ID if this was a new record
        if (result.create && result.id) {
          setFormCompletion(prevState => ({
            ...prevState,
            id: result.id
          }));

          // Update URL to include the new completion ID
          if (!completionId && !onClose) {
            navigate(`/form/compile/${formId}/${result.id}`, { replace: true });
          }
        }

        // Show success notification
        MySwal.fire({
          title: t('success', { ns: 'labels' }),
          text: t('formSavedSuccessfully', { ns: 'labels', defaultValue: 'Form saved successfully' }),
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        // Reset dirty flag after successful save
        setPreventQuit(false);
      } else {
        throw new Error(result?.error || t('failedToSaveForm', { ns: 'labels', defaultValue: 'Failed to save form' }));
      }
    } catch (error) {
      console.error("Error saving form completion:", error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: error.message || t('failedToSaveForm', { ns: 'labels', defaultValue: 'Failed to save form' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    }
  };

  // Handle form submission in simulation mode
  const handleSubmit = (e) => {
    e.preventDefault();

    // Don't allow submission in read-only mode
    if (effectiveReadOnly) return;

    if (isSimulation) {
      // Show success modal with blue confirmation button for simulation
      MySwal.fire({
        title: t('success', { ns: 'labels' }),
        text: t('formSubmittedSuccessfully', { ns: 'labels' }),
        icon: 'success',
        confirmButtonColor: '#17a2b8', // Using the info color (blue)
        confirmButtonText: 'OK'
      }).then(() => {
        // Close the simulation when user acknowledges
        if (onClose) onClose();
      });
    } else {
      // In compilation mode, save the form
      saveFormCompletion();
    }
  };

  // Handle form reset
  const handleClear = () => {
    // Don't allow clearing in read-only mode
    if (effectiveReadOnly) return;

    if (isSimulation) {
      // In simulation mode, just clear everything
      if (formRef.current) {
        formRef.current.reset();
        // Reset form values state
        setFormValues({});
        // Reset uploaded files
        setAnswerFiles({});
        // Reset links and observations
        setAnswerLinks({});
        setAnswerObservations({});
        // Reset text entries
        setAnswerTexts({});
        // Reset active tabs
        setActiveAttachmentTab({});
        // Force re-render to clear custom inputs that might not be handled by form.reset()
        setFormKey(prev => prev + 1);
      }
    } else {
      // In compilation mode, reload the last saved state
      MySwal.fire({
        title: t('reload', { ns: 'labels', defaultValue: 'Reload Form' }),
        text: t('reloadConfirmation', { ns: 'labels', defaultValue: 'This will reload the form with the last saved data. Any unsaved changes will be lost. Continue?' }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: colors?.primary || '#f05f23',
        cancelButtonColor: colors?.secondary || '#6c757d',
        confirmButtonText: t('yes', { ns: 'labels', defaultValue: 'Yes' }),
        cancelButtonText: t('cancel', { ns: 'labels', defaultValue: 'Cancel' })
      }).then((result) => {
        if (result.isConfirmed) {
          // Reset form values state
          setFormValues({});
          // Reset uploaded files
          setAnswerFiles({});
          // Reset links and observations
          setAnswerLinks({});
          setAnswerObservations({});
          // Reset text entries
          setAnswerTexts({});
          // Reset active tabs
          setActiveAttachmentTab({});
          // Force re-render
          setFormKey(prev => prev + 1);

          // Reload data from server
          loadFormData();

          // Reset preventQuit flag
          setPreventQuit(false);
        }
      });
    }
  };

  // Handle workflow step completion (for non-last actors in sequential workflow)
  const handleCompleteYourPart = async () => {
    // Don't allow submission in read-only mode
    if (effectiveReadOnly) return;

    // Don't allow submission in simulation mode
    if (isSimulation) return;

    // Ask for confirmation before completing
    const confirmResult = await MySwal.fire({
      title: t('confirmCompleteYourPart', { ns: 'labels', defaultValue: 'Conferma completamento' }),
      html: `
        <div class="text-start">
          <p>${t('confirmCompleteYourPartMessage', { ns: 'labels', defaultValue: 'Sei sicuro di voler completare la tua parte?' })}</p>
          <div class="alert alert-info mt-3">
            <i class="fa-solid fa-info-circle me-2"></i>
            <strong>${t('info', { ns: 'labels', defaultValue: 'Info' })}:</strong>
            ${t('nextActorWillBeNotified', { ns: 'labels', defaultValue: 'Il prossimo attore sarà notificato per completare la sua parte.' })}
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: colors?.primary || '#f05f23',
      cancelButtonColor: colors?.secondary || '#6c757d',
      confirmButtonText: t('yesComplete', { ns: 'labels', defaultValue: 'Sì, completa' }),
      cancelButtonText: t('cancel', { ns: 'labels', defaultValue: 'Annulla' }),
      reverseButtons: true
    });

    // If user cancels, return without completing
    if (!confirmResult.isConfirmed) return;

    try {
      // Show loading indicator during save operation
      MySwal.fire({
        title: t('saving', { ns: 'labels', defaultValue: 'Saving...' }),
        text: t('savingFormData', { ns: 'labels', defaultValue: 'Saving your form data' }),
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Collect answers from all questions (same logic as saveFormCompletion)
      const answersData = [];

      // Process all questions in the form
      const allQuestions = getAllQuestions();

      for (const question of allQuestions) {
        const questionId = question.id || question.questionCode;
        const questionValue = formValues[questionId];

        // Skip questions with no answers, not visible, or read-only for current actor
        // Special handling for date/time questions which have default values
        // Also keep questions that have attachments even if they have no value
        const hasQuestionAttachments = (answerFiles[questionId]?.length > 0) ||
          answerLinks[questionId] || answerTexts[questionId] || answerObservations[questionId];
        const shouldSkip = !isQuestionVisible(question) ||
          isQuestionReadOnlyCollaborative(questionId) ||
          (questionValue === undefined || questionValue === null) &&
          !['date', 'time', 'datetime'].includes(question.questionType) &&
          !hasQuestionAttachments;
        if (shouldSkip) continue;

        // Get localized question text
        const questionText = getLocalizedText(question.questionText, '');

        // Create standardized response object following backend data structure
        const questionResponse = {
          // Required fields (always present)
          questionId: questionId,
          questionType: question.questionType || '',
          questionText: questionText,

          // Answers array (always present, can be empty)
          answers: [],

          // Optional attachments (always present as fields, null if not used)
          files: (answerFiles[questionId] || []).map(f => ({ Id: f.Id, Name: f.Name, MimeType: f.MimeType, Size: f.Size })),
          file: answerFiles[questionId]?.[0]?.Id || null,
          fileName: answerFiles[questionId]?.[0]?.Name || null,
          fileMimeType: answerFiles[questionId]?.[0]?.MimeType || null,
          fileSize: answerFiles[questionId]?.[0]?.Size || null,

          link: answerLinks[questionId] || null,
          text: answerTexts[questionId] || null,
          observation: answerObservations[questionId] || null
        };

        // Handle answers based on question type following standardized structure
        if (question.questionType === 'vocabulary' && questionValue) {
          // GROUP 3 - VOCABULARY (supports multiple selection)
          const selectedValues = Array.isArray(questionValue) ? questionValue : [questionValue];

          selectedValues.forEach(value => {
            const vocItem = vocabularyItems[question.selectedVocabulary]?.find(v => v.Code === value);

            if (vocItem) {
              questionResponse.answers.push({
                answerId: vocItem.Code,
                answerText: vocItem.Name,
                answerType: 'vocabulary'
              });
            }
          });

          // For backward compatibility, set vocName to the first selected value
          if (selectedValues.length > 0) {
            const firstVocItem = vocabularyItems[question.selectedVocabulary]?.find(v => v.Code === selectedValues[0]);
            if (firstVocItem) {
              questionResponse.vocName = firstVocItem.Code;
            }
          }
        } else if (question.questionType === 'customVocabulary' && questionValue) {
          // GROUP 3 - CUSTOM VOCABULARY (supports multiple selection)
          const selectedValues = Array.isArray(questionValue) ? questionValue : [questionValue];

          for (let i = 0; i < selectedValues.length; i++) {
            const value = selectedValues[i];
            const customVocItem = customVocabularyItems[question.selectedCustomVocabulary]?.find(v => v.Code === value);

            const guid = await getGuid();
            questionResponse.answers.push({
              answerId: guid || '',
              vocValueText: customVocItem?.Name || value,
              answerType: 'customVocabulary',
              vocValueId: customVocItem?.Id || customVocItem?.Code || value
            });
          }

          // Add voc object with vocabulary parent information (for backward compatibility, use first selected)
          if (selectedValues.length > 0) {
            const firstCustomVocItem = customVocabularyItems[question.selectedCustomVocabulary]?.find(v => v.Code === selectedValues[0]);
            questionResponse.voc = {
              id: question.selectedCustomVocabulary,
              name: firstCustomVocItem?.Code || selectedValues[0]
            };
          }
        } else if ((question.questionType === 'multiple' || question.questionType === 'pdca') && Array.isArray(questionValue)) {
          // GROUP 5 - MULTIPLE CHOICE (including PDCA)
          questionValue.forEach(answerId => {
            const selectedAnswer = question.answers.find(a =>
              String(a.answerId || a.id) === String(answerId)
            );

            if (selectedAnswer) {
              questionResponse.answers.push({
                answerId: answerId,
                answerText: getLocalizedText(selectedAnswer.answerText, ''),
                answerType: 'predefined'
              });
            }
          });
        } else if (['single', 'boolean', 'yesNoNA', 'trueFalse'].includes(question.questionType)) {
          // GROUP 1 - SINGLE CHOICE
          const selectedAnswer = question.answers.find(a =>
            String(a.answerId || a.id) === String(questionValue)
          );

          if (selectedAnswer) {
            questionResponse.answers.push({
              answerId: questionValue,
              answerText: getLocalizedText(selectedAnswer.answerText, ''),
              answerType: 'predefined'
            });
          }
        } else if (question.questionType === 'open' || question.questionType === 'openNumber') {
          // GROUP 2 - FREE INPUT - Generate GUID for answerId
          const guid = await getGuid();
          questionResponse.answers.push({
            answerId: guid || '',  // Generated GUID or empty if failed
            answerText: questionValue,
            answerType: 'userInput'
          });
        } else if (['date', 'time', 'datetime'].includes(question.questionType)) {
          // GROUP 4 - DATE/TIME - Generate GUID for answerId
          const guid = await getGuid();

          // Use default value if questionValue is undefined
          let answerText = questionValue;
          if (answerText === undefined || answerText === null) {
            // Apply same default values as in the input fields
            if (question.questionType === 'date') {
              answerText = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
            } else if (question.questionType === 'time') {
              answerText = new Date().toTimeString().split(' ')[0].substr(0, 5); // HH:MM
            } else if (question.questionType === 'datetime') {
              answerText = new Date().toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
            }
          }

          questionResponse.answers.push({
            answerId: guid || '',  // Generated GUID or empty if failed
            answerText: answerText,
            answerType: 'userInput'
          });
        } else if (question.questionType === 'period') {
          // GROUP 8 - PERIOD - Generate GUID and store start|end
          const guid = await getGuid();
          const raw = formValues[questionId] || '';
          questionResponse.answers.push({
            answerId: guid || '',
            answerText: String(raw),
            answerType: 'period'
          });
        } else if (question.questionType === 'departmentFunctionalArea' && questionValue) {
          // GROUP 6 - DEPARTMENT/FUNCTIONAL AREA - Generate GUID for answerId
          const guid = await getGuid();

          questionResponse.answers.push({
            answerId: guid || '',  // Generated GUID or empty if failed
            answerText: questionValue, // Combined value: "departmentId|functionalAreaId"
            answerType: 'userInput'
          });
        } else if (question.questionType === 'table' && Array.isArray(questionValue)) {
          // GROUP 7 - TABLE - Generate GUID for answerId
          const guid = await getGuid();

          questionResponse.answers.push({
            answerId: guid || '',  // Generated GUID or empty if failed
            answerText: JSON.stringify(questionValue), // Serialize table data as JSON
            answerType: 'tableData'
          });
        }

        // Add to answers array only if there are meaningful contents
        if (questionResponse.answers.length > 0 ||
          questionResponse.file !== null ||
          questionResponse.link !== null ||
          questionResponse.text !== null ||
          questionResponse.observation !== null) {
          answersData.push(questionResponse);
        }
      }

      // Create or update form completion with proper data structure
      const compilationData = {
        ...formCompletion,
        data: {
          // Preserve existing data structure from backend
          ...(formCompletion?.data || {}),
          answers: answersData,
          // Update timestamp information
          lastSaved: new Date().toISOString()
        }
      };

      // Prepare body with workflow information
      // Extract only actor information from collaborativeFormsSchema
      const actors = collaborativeFormsSchema?.map(schema => schema.actor) || [];

      const requestBody = {
        formCompletion: compilationData,
        completeActorPart: true,  // Flag to indicate actor completed their part
        collaborativeAssignmentId: collaborativeAssignmentId || null,  // ID from Collaborative_Forms_Assignments
        formAssignmentId: formAssignmentId || null,  // ID from FormAssignment
        actors: actors  // List of actors involved in this collaborative form
      };

      // Send save request to backend with completeActorPart flag and workflow IDs
      const result = await api.sendMessage("darm/formCompletion/save", {
        user: currentUser(),
        body: requestBody
      });

      if (result?.success) {
        // Update completion ID if this was a new record
        if (result.create && result.id) {
          setFormCompletion(prevState => ({
            ...prevState,
            id: result.id
          }));

          // Update URL to include the new completion ID
          if (!completionId && !onClose) {
            navigate(`/form/compile/${formId}/${result.id}`, { replace: true });
          }
        }

        // Show success notification
        MySwal.fire({
          title: t('success', { ns: 'labels' }),
          text: t('actorPartCompletedSuccessfully', { ns: 'labels', defaultValue: 'La tua parte è stata completata con successo. Il prossimo attore sarà notificato.' }),
          icon: 'success',
          timer: 3000,
          showConfirmButton: true,
          confirmButtonColor: colors?.success || '#28a745',
          confirmButtonText: 'OK'
        }).then(() => {
          // Reset dirty flag after successful save
          setPreventQuit(false);

          // Navigate back to form builder dashboard (same behavior as submit answers)
          if (onClose) {
            onClose();
          } else {
            // Navigate back to form builder dashboard
            navigate('/form_builder/dashboard');
          }
        });

      } else {
        throw new Error(result?.error || t('failedToCompleteActorPart', { ns: 'labels', defaultValue: 'Failed to complete your part' }));
      }
    } catch (error) {
      console.error("Error completing actor part:", error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: error.message || t('failedToCompleteActorPart', { ns: 'labels', defaultValue: 'Failed to complete your part' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    }
  };

  // Handle form submission for compilation mode (submit answers and mark as compiled)
  const handleSubmitAnswers = async () => {
    // Don't allow submission in read-only mode
    if (effectiveReadOnly) return;

    // Don't allow submission in simulation mode (this should use handleSubmit instead)
    if (isSimulation) return;

    // Ask for confirmation before submitting
    const confirmResult = await MySwal.fire({
      title: t('confirmSubmission', { ns: 'labels', defaultValue: 'Conferma invio' }),
      html: `
        <div class="text-start">
          <p>${t('confirmSubmissionMessage', { ns: 'labels', defaultValue: 'Sei sicuro di voler inviare le risposte?' })}</p>
          <div class="alert alert-warning mt-3">
            <i class="fa-solid fa-exclamation-triangle me-2"></i>
            <strong>${t('warning', { ns: 'labels', defaultValue: 'Attenzione' })}:</strong>
            ${t('answersNotModifiable', { ns: 'labels', defaultValue: 'Le risposte inviate non saranno più modificabili.' })}
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: colors?.success || '#28a745',
      cancelButtonColor: colors?.secondary || '#6c757d',
      confirmButtonText: t('yesSubmit', { ns: 'labels', defaultValue: 'Sì, invia' }),
      cancelButtonText: t('cancel', { ns: 'labels', defaultValue: 'Annulla' }),
      reverseButtons: true
    });

    // If user cancels, return without submitting
    if (!confirmResult.isConfirmed) return;

    // Validate mandatory sections and questions before submitting
    const validation = validateMandatorySections();
    if (!validation.isValid) {
      // Show validation errors
      let errorMessage = '';

      validation.errors.forEach((error, index) => {
        if (error.isIndividualQuestion) {
          // Individual mandatory question in non-mandatory section
          errorMessage += `${t('mandatoryQuestionInSection', { ns: 'labels' })} "${error.sectionName}":\n`;
          errorMessage += `${error.incompleteMandatoryQuestions.join(', ')}\n`;
        } else {
          // Mandatory section with incomplete questions
          errorMessage += t('mandatorySectionError', {
            ns: 'labels',
            sectionName: error.sectionName
          }) + '\n';
          errorMessage += `${t('incompleteMandatoryQuestions', { ns: 'labels' })}: ${error.incompleteMandatoryQuestions.join(', ')}\n`;
        }

        if (index < validation.errors.length - 1) {
          errorMessage += '\n';
        }
      });

      MySwal.fire({
        title: t('validationError', { ns: 'labels' }),
        text: errorMessage,
        icon: 'warning',
        confirmButtonColor: colors?.warning || '#ffc107',
        confirmButtonText: 'OK'
      });

      return; // Don't submit if validation fails
    }

    try {
      // Show loading indicator
      MySwal.fire({
        title: t('submitting', { ns: 'labels', defaultValue: 'Submitting...' }),
        text: t('submittingFormData', { ns: 'labels', defaultValue: 'Submitting your form data' }),
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Array of answers for all questions
      const answersData = [];

      // Gather all answers data (same logic as saveFormCompletion)
      const allQuestions = getAllQuestions();

      for (const question of allQuestions) {
        const questionId = question.id || question.questionCode;
        const questionValue = formValues[questionId];

        // Skip questions with no answers or not visible
        // For date/time/datetime questions, allow processing even if questionValue is undefined (they have default values)
        // Also keep questions that have attachments even if they have no value
        const hasQuestionAttachments = (answerFiles[questionId]?.length > 0) ||
          answerLinks[questionId] || answerTexts[questionId] || answerObservations[questionId];
        const shouldSkip = !isQuestionVisible(question) ||
          (questionValue === undefined || questionValue === null) &&
          !['date', 'time', 'datetime'].includes(question.questionType) &&
          !hasQuestionAttachments;
        if (shouldSkip) continue;

        // Get the question text in current language
        const questionText = getLocalizedText(question.questionText, '');

        // Create standardized response object for this question
        const questionResponse = {
          // CAMPI OBBLIGATORI (sempre presenti)
          questionId: questionId,
          questionType: question.questionType || '',
          questionText: questionText,

          // ARRAY RISPOSTE (sempre presente, può essere vuoto)
          answers: [],

          // ALLEGATI OPZIONALI (sempre presenti come campi, null se non utilizzati)
          files: (answerFiles[questionId] || []).map(f => ({ Id: f.Id, Name: f.Name, MimeType: f.MimeType, Size: f.Size })),
          file: answerFiles[questionId]?.[0]?.Id || null,
          fileName: answerFiles[questionId]?.[0]?.Name || null,
          fileMimeType: answerFiles[questionId]?.[0]?.MimeType || null,
          fileSize: answerFiles[questionId]?.[0]?.Size || null,

          link: answerLinks[questionId] || null,
          text: answerTexts[questionId] || null,
          observation: answerObservations[questionId] || null
        };

        // Handle answers based on question type following standardized structure
        if (question.questionType === 'vocabulary' && questionValue) {
          // GRUPPO 3 - VOCABOLARIO (supports multiple selection)
          const selectedValues = Array.isArray(questionValue) ? questionValue : [questionValue];

          selectedValues.forEach(value => {
            const vocItem = vocabularyItems[question.selectedVocabulary]?.find(v => v.Code === value);

            if (vocItem) {
              questionResponse.answers.push({
                answerId: vocItem.Code,
                answerText: vocItem.Name,
                answerType: 'vocabulary'
              });
            }
          });

          // For backward compatibility, set vocName to the first selected value
          if (selectedValues.length > 0) {
            const firstVocItem = vocabularyItems[question.selectedVocabulary]?.find(v => v.Code === selectedValues[0]);
            if (firstVocItem) {
              questionResponse.vocName = firstVocItem.Code;
            }
          }
        } else if (question.questionType === 'customVocabulary' && questionValue) {
          // GRUPPO 3 - VOCABOLARIO PERSONALIZZATO (supports multiple selection)
          const selectedValues = Array.isArray(questionValue) ? questionValue : [questionValue];

          for (let i = 0; i < selectedValues.length; i++) {
            const value = selectedValues[i];
            const customVocItem = customVocabularyItems[question.selectedCustomVocabulary]?.find(v => v.Code === value);

            const guid = await getGuid();
            questionResponse.answers.push({
              answerId: guid || '',
              vocValueText: customVocItem?.Name || value,
              answerType: 'customVocabulary',
              vocValueId: customVocItem?.Id || customVocItem?.Code || value
            });
          }

          // Add voc object with vocabulary parent information (for backward compatibility, use first selected)
          if (selectedValues.length > 0) {
            const firstCustomVocItem = customVocabularyItems[question.selectedCustomVocabulary]?.find(v => v.Code === selectedValues[0]);
            questionResponse.voc = {
              id: question.selectedCustomVocabulary,
              name: firstCustomVocItem?.Code || selectedValues[0]
            };
          }
        } else if ((question.questionType === 'multiple' || question.questionType === 'pdca') && Array.isArray(questionValue)) {
          // GRUPPO 5 - SCELTA MULTIPLA (including PDCA)
          questionValue.forEach(answerId => {
            const selectedAnswer = question.answers.find(a =>
              String(a.answerId || a.id) === String(answerId)
            );

            if (selectedAnswer) {
              questionResponse.answers.push({
                answerId: answerId,
                answerText: getLocalizedText(selectedAnswer.answerText, ''),
                answerType: 'predefined'
              });
            }
          });
        } else if (['single', 'boolean', 'yesNoNA', 'trueFalse'].includes(question.questionType)) {
          // GRUPPO 1 - SCELTA SINGOLA
          const selectedAnswer = question.answers.find(a =>
            String(a.answerId || a.id) === String(questionValue)
          );

          if (selectedAnswer) {
            questionResponse.answers.push({
              answerId: questionValue,
              answerText: getLocalizedText(selectedAnswer.answerText, ''),
              answerType: 'predefined'
            });
          }
        } else if (question.questionType === 'open' || question.questionType === 'openNumber') {
          // GRUPPO 2 - INPUT LIBERO - Generate GUID for answerId
          const guid = await getGuid();
          questionResponse.answers.push({
            answerId: guid || '',  // GUID generato o vuoto se fallisce
            answerText: questionValue,
            answerType: 'userInput'
          });
        } else if (['date', 'time', 'datetime'].includes(question.questionType)) {
          // GRUPPO 4 - DATA/ORA - Generate GUID for answerId
          const guid = await getGuid();

          // If questionValue is undefined, use the default value
          let answerText = questionValue;
          if (answerText === undefined || answerText === null) {
            // Use the same default values as in the input fields
            if (question.questionType === 'date') {
              answerText = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
            } else if (question.questionType === 'time') {
              answerText = new Date().toTimeString().split(' ')[0].substr(0, 5); // HH:MM
            } else if (question.questionType === 'datetime') {
              answerText = new Date().toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
            }
          }

          questionResponse.answers.push({
            answerId: guid || '',  // GUID generato o vuoto se fallisce
            answerText: answerText,
            answerType: 'userInput'
          });
        } else if (question.questionType === 'departmentFunctionalArea' && questionValue) {
          // GRUPPO 6 - DIPARTIMENTO/AREA FUNZIONALE - Generate GUID for answerId
          const guid = await getGuid();

          questionResponse.answers.push({
            answerId: guid || '',  // GUID generato o vuoto se fallisce
            answerText: questionValue, // Combined value: "departmentId|functionalAreaId"
            answerType: 'userInput'
          });
        } else if (question.questionType === 'table' && Array.isArray(questionValue)) {
          // GRUPPO 7 - TABELLA - Generate GUID for answerId
          const guid = await getGuid();

          questionResponse.answers.push({
            answerId: guid || '',  // GUID generato o vuoto se fallisce
            answerText: JSON.stringify(questionValue), // Serialize table data as JSON
            answerType: 'tableData'
          });
        }

        // Add to answers array only if there are answers or attachments
        // Con la struttura standardizzata, controlliamo se ci sono contenuti significativi
        if (questionResponse.answers.length > 0 ||
          questionResponse.file !== null ||
          questionResponse.link !== null ||
          questionResponse.text !== null ||
          questionResponse.observation !== null) {
          answersData.push(questionResponse);
        }
      }

      // Create or update form completion with proper data structure
      const compilationData = {
        ...formCompletion,
        data: {
          // Preserve existing data structure from backend
          ...(formCompletion?.data || {}),
          answers: answersData,
          // Update last modified info
          lastSaved: new Date().toISOString()
        }
      };

      // Extract only actor information from collaborativeFormsSchema
      const actors = collaborativeFormsSchema?.map(schema => schema.actor) || [];

      // Prepare base body for API call
      const requestBody = {
        formCompletion: compilationData,
        compiled: true  // This will trigger the markAsSubmitted method on the backend
      };

      // Add collaborative form parameters only if this is a collaborative form
      if (isCollaborative) {
        requestBody.completeActorPart = true;  // Flag to indicate actor completed their part
        requestBody.actors = actors;  // List of actors involved in this collaborative form
        requestBody.collaborativeAssignmentId = collaborativeAssignmentId || null;  // ID from Collaborative_Forms_Assignments
        requestBody.formAssignmentId = formAssignmentId || null;  // ID from FormAssignment
      }

      // Call the API with compiled: true to mark as submitted
      const result = await api.sendMessage("darm/formCompletion/save", {
        user: currentUser(),
        body: requestBody
      });

      if (result?.success) {
        // Update the completion ID if this was a new record
        if (result.create && result.id) {
          setFormCompletion(prevState => ({
            ...prevState,
            id: result.id
          }));

          // Update URL if needed to include the new completion ID
          if (!completionId && !onClose) {
            navigate(`/form/compile/${formId}/${result.id}`, { replace: true });
          }
        }

        // Show success message for submission
        MySwal.fire({
          title: t('success', { ns: 'labels' }),
          text: t('formSubmittedSuccessfully', { ns: 'labels', defaultValue: 'Form submitted successfully' }),
          icon: 'success',
          timer: 3000,
          showConfirmButton: true,
          confirmButtonColor: colors?.success || '#28a745',
          confirmButtonText: 'OK'
        }).then(() => {
          // Reset preventQuit after successful submission
          setPreventQuit(false);

          // Optionally navigate away or close the form after submission
          if (onClose) {
            onClose();
          } else {
            // Navigate back to form list or dashboard
            navigate('/forms');
          }
        });

      } else {
        throw new Error(result?.error || t('failedToSubmitForm', { ns: 'labels', defaultValue: 'Failed to submit form' }));
      }
    } catch (error) {
      console.error("Error submitting form completion:", error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: error.message || t('failedToSubmitForm', { ns: 'labels', defaultValue: 'Failed to submit form' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    }
  };

  /**
   * File Selection Modal Function
   * 
   * Opens a modal dialog for selecting files from existing uploads or uploading new files
   * Provides both file browser and drag & drop functionality for file attachment
   * 
   * @param {string} questionId - ID of the question to attach the file to
   */
  const selectAnswerFile = async (questionId) => {
    // Prevent file selection in read-only mode or collaborative readonly
    if (effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)) return;

    if (isSimulation) {
      MySwal.fire({
        title: t('simulationMode', { ns: 'labels', defaultValue: 'Simulation Mode' }),
        text: t('fileSelectionDisabledInSim', { ns: 'labels', defaultValue: 'File selection is disabled in simulation mode.' }),
        icon: 'info',
        confirmButtonColor: colors?.info || '#17a2b8'
      });
      return;
    }

    // Set context for file upload operations
    setCurrentQuestionId(questionId);

    const dragClassPrefix = isSimulation ? 'sim' : 'comp';

    // Create file selection modal with dynamic content
    let _modal = window.$(`<div id="select-answer-file-modal" class="modal modal-xl" tabindex="-1">
      <div class="modal-dialog modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">${t('selectFile', { ns: 'labels' })}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex justify-content-center">
              <div class="text-center my-3">
                <img src="${waiting}" style="width: 50px;"/> ${t('loadingFiles', { ns: 'labels' })}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${t('close', { ns: 'labels' })}</button>
            <button type="button" class="btn btn-primary" id="upload-new-file-btn">
              <i class="fa fa-upload me-1"></i> ${t('uploadFromDevice', { ns: 'labels' })}
            </button>
            <input type="file" id="device-file-input" style="display: none;" />
          </div>
        </div>
      </div>
    </div>`);

    // Remove any existing modal and show the new one
    window.$("#select-answer-file-modal").remove();
    window.$("body").append(_modal);
    _modal.modal("show");
    const modal_body = _modal.find(".modal-body");

    // Setup hidden file input for device uploads
    const fileInput = window.$("#device-file-input");

    // Handle "Upload file from device" button click
    window.$("#upload-new-file-btn").on("click", () => {
      fileInput.trigger('click');
    });

    // Handle file selection from device
    fileInput.on('change', (e) => {
      if (e.target.files && e.target.files.length > 0) {
        // Close the modal
        window.$("#select-answer-file-modal").modal("hide");

        // Validate answers folder availability
        if (!answersFolderId) {
          MySwal.fire({
            title: t('error', { ns: 'labels' }),
            text: t('failedToRetrieveFiles', { ns: 'labels' }),
            icon: 'error',
            confirmButtonColor: colors?.danger || '#dc3545'
          });
          return;
        }

        // Show upload progress indicator
        MySwal.fire({
          title: t('uploading', { ns: 'labels' }),
          text: e.target.files[0].name,
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          }
        });

        // Upload the file using the upload manager
        uploadManager.fileUpload({
          user: currentUser(),
          parentId: answersFolderId,
          files: Array.from(e.target.files),
          onFileAdded: (files) => {
            if (Array.isArray(files) && files.length > 0) {
              // Append all uploaded files to the array
              setAnswerFiles(prev => ({
                ...prev,
                [questionId]: [...(prev[questionId] || []), ...files]
              }));

              // Show success notification
              MySwal.fire({
                title: t('fileUploadSuccessful', { ns: 'labels' }),
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
              });
            }
          },
          onError: (error) => {
            console.error("Upload error:", error);
            MySwal.fire({
              title: t('error', { ns: 'labels' }),
              text: error.message || t('failedToRetrieveFiles', { ns: 'labels' }),
              icon: 'error',
              confirmButtonColor: colors?.danger || '#dc3545'
            });
          }
        });
      }
    });

    try {
      // Check if we have a valid folder ID
      if (!answersFolderId) {
        modal_body.html(`<div class="alert alert-danger">${t('failedToRetrieveFiles', { ns: 'labels' })}</div>`);
        return;
      }

      // Load files from the answers folder
      const result = await api.sendMessage("dms/getFolder", {
        user: currentUser(),
        body: {
          id: answersFolderId,
          addPath: true
        }
      });

      if (result?.content?.files && Array.isArray(result.content.files)) {
        // Clear loading message
        modal_body.empty();

        // Brief description before the file list
        //modal_body.append(`<p class="text-muted small mb-3">Seleziona un file dalla lista sottostante per allegarlo alla domanda. I file già allegati non sono mostrati.</p>`);
        modal_body.append(`<p class="text-muted small mb-3">` + t('modalDescriptionDetail', { ns: 'labels' }) + `</p>`);

        // Create a list to display files
        const fileList = window.$('<div class="list-group"></div>');
        modal_body.append(fileList);

        // Filter out files already attached to this question
        const alreadyAttachedIds = new Set((answerFiles[questionId] || []).map(f => f.Id));
        const availableFiles = result.content.files.filter(f => !alreadyAttachedIds.has(f.Id));

        if (availableFiles.length === 0) {
          fileList.append(`<div class="list-group-item text-center py-3">${t('noFilesFound', { ns: 'labels' })}</div>`);
        } else {
          // Add each file to the list
          availableFiles.forEach(file => {
            // Normalize dates
            if (file.ItemLastUpdated) file.ItemLastUpdated = new Date(file.ItemLastUpdated);
            if (file.ItemCreated) file.ItemCreated = new Date(file.ItemCreated);
            if (file.ItemDeleted) file.ItemDeleted = new Date(file.ItemDeleted);

            // Get appropriate icon for file type
            const fileIcon = icons.iconMapping(file.MimeType);

            // Create list item
            const listItem = window.$(`
              <div class="list-group-item list-group-item-action d-flex align-items-center">
                <div class="me-3">
                  <img src="${fileIcon}" alt="File icon" style="width: 24px; height: 24px;">
                </div>
                <div class="flex-grow-1">
                  <h6 class="mb-1">${file.Name}</h6>
                  <small class="text-muted">
                    ${t('lastUpdated', { ns: 'labels' })}: ${(file.ItemLastUpdated || file.ItemCreated).toLocaleDateString()}
                    ${file.Size ? ` | ${t('fileSize', { ns: 'labels' })}: ${Math.round(file.Size / 1024)} KB` : ''}
                  </small>
                </div>
              </div>
            `);

            // Create action buttons for each file
            const actionButtons = window.$(`
              <div class="btn-group ms-2">
                <button type="button" class="btn btn-sm btn-outline-primary" title="${t('openFile', { ns: 'labels', defaultValue: 'Open File' })}">
                  <i class="fa-solid fa-eye"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-success" title="${t('selectFile', { ns: 'labels', defaultValue: 'Select File' })}">
                  <i class="fa-solid fa-check"></i>
                </button>
              </div>
            `);

            // Add the action buttons to the list item
            listItem.find('.flex-grow-1').after(actionButtons);

            // Remove the click event from the entire list item
            listItem.css('cursor', 'default');

            // Add click event for opening file
            actionButtons.find('.btn-outline-primary').on('click', (e) => {
              e.stopPropagation();
              handleFileOpen(file);
            });

            // Add click event for selecting file
            actionButtons.find('.btn-outline-success').on('click', (e) => {
              e.stopPropagation();

              // Append the selected file to the array
              setAnswerFiles(prev => ({
                ...prev,
                [questionId]: [...(prev[questionId] || []), file]
              }));

              // Show a success message
              MySwal.fire({
                title: t('fileSelected', { ns: 'labels' }),
                text: file.Name,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
              });

              // Close the modal
              window.$("#select-answer-file-modal").modal("hide");
            });

            fileList.append(listItem);
          });
        }
      } else {
        modal_body.html(`<div class="alert alert-warning">${t('failedToRetrieveFiles', { ns: 'labels' })}</div>`);
      }
    } catch (error) {
      console.error("Error loading files:", error);
      modal_body.html(`<div class="alert alert-danger">${error.message || t('failedToRetrieveFiles', { ns: 'labels' })}</div>`);
    }
  };







  // Aggiungiamo il controllo per il beforeunload quando ci sono modifiche non salvate
  useEffect(() => {
    if (preventQuit) {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = '';
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [preventQuit]);

  // Create render functions using the new components
  const renderFileUploadUI = (questionId, isEmbedded = false, question = null) => (
    <FileUploadUI
      questionId={questionId}
      answerFiles={answerFiles}
      setAnswerFiles={setAnswerFiles}
      handleInputChange={handleInputChange}
      selectAnswerFile={selectAnswerFile}
      handleFileOpen={handleFileOpen}
      handleFileDownload={handleFileDownload}
      isEmbedded={isEmbedded}
      isSimulation={isSimulation}
      readOnly={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
      dragAnimationRef={dragAnimationRef}
      _frDraggingEffect={_frDraggingEffect}
      hideDragBubble={hideDragBubble}
      uploadManager={uploadManager}
      answersFolderId={answersFolderId}
      currentUser={currentUser}
      MySwal={MySwal}
      colors={colors}
      t={t}
      maxFiles={question?.maxFiles ?? null}
    />
  );

  const renderLinkUI = (questionId) => (
    <LinkUI
      questionId={questionId}
      answerLinks={answerLinks}
      setAnswerLinks={setAnswerLinks}
      handleInputChange={handleInputChange}
      readOnly={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
      MySwal={MySwal}
      colors={colors}
    />
  );

  const renderTextUI = (questionId) => (
    <TextUI
      questionId={questionId}
      answerTexts={answerTexts}
      setAnswerTexts={setAnswerTexts}
      handleInputChange={handleInputChange}
      readOnly={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
      MySwal={MySwal}
      isSimulation={isSimulation}
    />
  );

  const renderObservationUI = (questionId) => (
    <ObservationUI
      questionId={questionId}
      answerObservations={answerObservations}
      setAnswerObservations={setAnswerObservations}
      handleInputChange={handleInputChange}
      readOnly={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
      MySwal={MySwal}
      isSimulation={isSimulation}
    />
  );

  const renderAttachmentArea = (question, questionId) => (
    <AttachmentArea
      question={question}
      questionId={questionId}
      answerFiles={answerFiles}
      answerLinks={answerLinks}
      answerTexts={answerTexts}
      activeAttachmentTab={activeAttachmentTab}
      setActiveAttachmentTab={setActiveAttachmentTab}
      readOnly={effectiveReadOnly}
      renderFileUploadUI={renderFileUploadUI}
      renderLinkUI={renderLinkUI}
      renderTextUI={renderTextUI}
      renderObservationUI={renderObservationUI}
    />
  );

  // Render the UI based on the mode
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Lock state handling for collaborative forms (only in compilation mode)
  if (!isSimulation && isCollaborative && lockState) {
    // Show connecting state
    if (lockState.connecting) {
      return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>
          <main className="flex-grow-1 overflow-auto d-flex align-items-center justify-content-center" style={{ minHeight: 0, backgroundColor: colors?.light || '#f8f9fa' }}>
            <div className="container-fluid py-4">
              <div className="row justify-content-center">
                <div className="col-xl-5 col-lg-6 col-md-8">
                  <div className="card shadow-sm border-0">
                    <div className="card-header text-center py-4" style={{ backgroundColor: colors?.secondaryLight || '#e9ecef', borderBottom: `3px solid ${colors?.primary || '#f05f23'}` }}>
                      <div className="mb-3">
                        <div className="spinner-border" style={{ color: colors?.primary || '#f05f23', width: '3rem', height: '3rem' }} role="status">
                          <span className="visually-hidden">{t('acquiringLock', { ns: 'labels', defaultValue: 'Acquiring lock...' })}</span>
                        </div>
                      </div>
                      <h4 className="mb-0" style={{ color: colors?.dark || '#343a40' }}>
                        {t('acquiringLock', { ns: 'labels', defaultValue: 'Acquisizione blocco...' })}
                      </h4>
                    </div>
                    <div className="card-body text-center py-4">
                      <p className="text-muted mb-0">
                        <i className="fa-solid fa-info-circle me-2"></i>
                        {t('acquiringLockMessage', { ns: 'labels', defaultValue: 'Attendere mentre si acquisisce il blocco del form collaborativo' })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      );
    }

    // Show locked state (form is locked by another user)
    if (lockState.isLocked) {
      return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>
          <main className="flex-grow-1 overflow-auto d-flex align-items-center justify-content-center" style={{ minHeight: 0, backgroundColor: colors?.light || '#f8f9fa' }}>
            <div className="container-fluid py-4">
              <div className="row justify-content-center">
                <div className="col-xl-6 col-lg-7 col-md-9">
                  <div className="card shadow border-0" style={{ borderLeft: `5px solid ${colors?.warning || '#ffc107'}` }}>
                    <div className="card-header d-flex align-items-center py-3" style={{ backgroundColor: colors?.secondaryLight || '#e9ecef' }}>
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: '50px',
                          height: '50px',
                          backgroundColor: colors?.warning || '#ffc107',
                          color: colors?.dark || '#343a40'
                        }}>
                        <i className="fa-solid fa-lock fa-lg"></i>
                      </div>
                      <div>
                        <h5 className="mb-0" style={{ color: colors?.dark || '#343a40' }}>
                          {t('formLocked', { ns: 'labels', defaultValue: 'Form in uso' })}
                        </h5>
                        <small className="text-muted">
                          {t('formLockedBy', { ns: 'labels', defaultValue: 'In modifica da' })}: <strong>{lockState.lockedBy?.userName || 'Unknown User'}</strong>
                        </small>
                      </div>
                    </div>
                    <div className="card-body p-4">
                      <div className="alert mb-4" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)', borderColor: colors?.warning || '#ffc107', color: colors?.dark || '#343a40' }}>
                        <i className="fa-solid fa-user-edit me-2"></i>
                        {t('formLockedMessage', { ns: 'labels', defaultValue: 'Questo form è attualmente in modifica da un altro utente. Non è possibile modificarlo in questo momento.' })}
                      </div>
                      <div className="d-flex gap-3 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-secondary px-4"
                          onClick={() => navigate('/form_builder/dashboard')}
                        >
                          <i className="fa-solid fa-arrow-left me-2"></i>
                          {t('backToList', { ns: 'labels', defaultValue: 'Torna alla lista' })}
                        </button>
                        <button
                          type="button"
                          className="btn px-4"
                          style={{ backgroundColor: colors?.primary || '#f05f23', color: colors?.white || '#ffffff', borderColor: colors?.primary || '#f05f23' }}
                          onClick={() => window.location.reload()}
                        >
                          <i className="fa-solid fa-sync me-2"></i>
                          {t('retryLock', { ns: 'labels', defaultValue: 'Riprova' })}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      );
    }

    // Show error state
    if (lockState.error) {
      return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>
          <main className="flex-grow-1 overflow-auto d-flex align-items-center justify-content-center" style={{ minHeight: 0, backgroundColor: colors?.light || '#f8f9fa' }}>
            <div className="container-fluid py-4">
              <div className="row justify-content-center">
                <div className="col-xl-6 col-lg-7 col-md-9">
                  <div className="card shadow border-0" style={{ borderLeft: `5px solid ${colors?.danger || '#dc3545'}` }}>
                    <div className="card-header d-flex align-items-center py-3" style={{ backgroundColor: colors?.secondaryLight || '#e9ecef' }}>
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: '50px',
                          height: '50px',
                          backgroundColor: colors?.danger || '#dc3545',
                          color: colors?.white || '#ffffff'
                        }}>
                        <i className="fa-solid fa-exclamation-triangle fa-lg"></i>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="mb-0" style={{ color: colors?.dark || '#343a40' }}>
                          {t('lockError', { ns: 'labels', defaultValue: 'Errore di blocco' })}
                        </h5>
                        <small className="text-muted">
                          {t('lockErrorMessage', { ns: 'labels', defaultValue: 'Si è verificato un errore durante l\'acquisizione del blocco del form' })}
                        </small>
                      </div>
                    </div>
                    <div className="card-body p-4">
                      <div className="alert mb-4" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)', borderColor: colors?.danger || '#dc3545', color: colors?.dark || '#343a40' }}>
                        <i className="fa-solid fa-bug me-2"></i>
                        <strong>{t('error', { ns: 'labels', defaultValue: 'Errore' })}:</strong>
                        <div className="mt-2">
                          <code className="text-danger small">{lockState.error}</code>
                        </div>
                      </div>
                      <div className="d-flex gap-3 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-secondary px-4"
                          onClick={() => navigate('/form_builder/dashboard')}
                        >
                          <i className="fa-solid fa-arrow-left me-2"></i>
                          {t('backToList', { ns: 'labels', defaultValue: 'Torna alla lista' })}
                        </button>
                        <button
                          type="button"
                          className="btn px-4"
                          style={{ backgroundColor: colors?.primary || '#f05f23', color: colors?.white || '#ffffff', borderColor: colors?.primary || '#f05f23' }}
                          onClick={() => window.location.reload()}
                        >
                          <i className="fa-solid fa-sync me-2"></i>
                          {t('retryLock', { ns: 'labels', defaultValue: 'Riprova' })}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      );
    }
  }

  if (isSimulation) {
    // Simulation mode UI - without header, directly showing content
    return (
      <div className="d-flex flex-column" style={{ height: '100vh' }}>

        {/* Main scrollable content - no header in simulation */}
        <main className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
          <div className="container-fluid py-3">
            <div className="row">
              {/* Main form content - identical width logic to compilation mode */}
              <div className={(shouldShowUserGuideVisible() || (shouldShowContextData() && !isContextCollapsed)) ? "col-xl-8 col-lg-7" : "col-xl-12"}>

                {/* Show guide availability indicator when guide is collapsed */}
                {shouldShowUserGuide() && isGuideCollapsed && (
                  <div className="alert alert-info d-flex justify-content-between align-items-center py-2 mb-3">
                    <div className="d-flex align-items-center">
                      <i className="fa-solid fa-book-open me-2"></i>
                      <span>{t('userGuideAvailable', { ns: 'labels', defaultValue: 'Guida utente disponibile' })}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={toggleGuideCollapse}
                    >
                      <i className="fa-solid fa-eye me-1"></i>
                      {t('showGuide', { ns: 'labels', defaultValue: 'Mostra Guida' })}
                    </button>
                  </div>
                )}

                <div className="card h-auto">
                  <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: colors?.secondaryLight || '#e9ecef' }}>
                    <h5 className="mb-0">Simulazione Form</h5>
                    {onClose && (
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                        <i className="fa-solid fa-xmark me-1"></i>
                        Chiudi simulazione
                      </button>
                    )}
                  </div>
                  <div className="card-body p-3">
                    {/* Simulation info alert */}
                    <div className="alert alert-info">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      Questa è una simulazione del form. Le risposte non saranno salvate.
                    </div>

                    {/* Actor selector for collaborative forms */}
                    {propForm?.IsCollaborative && propForm?.CollaborativeFormsSchema?.length > 0 && (
                      <div className="alert alert-secondary d-flex align-items-center gap-3 py-2">
                        <i className="fa-solid fa-user-tag"></i>
                        <span className="fw-semibold text-nowrap">Simula come attore:</span>
                        <select
                          className="form-select form-select-sm w-auto"
                          value={simulationActorId ?? propForm.CollaborativeFormsSchema[0]?.actor?.id ?? ''}
                          onChange={e => setSimulationActorId(e.target.value)}
                        >
                          {propForm.CollaborativeFormsSchema.map(s => (
                            <option key={s.actor.id} value={s.actor.id}>
                              {s.actor.name || `Attore ${s.actor.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {effectiveReadOnly && (
                      <div className="alert alert-warning">
                        <i className="fa-solid fa-lock me-2"></i>
                        {t('readOnlyInfo', { ns: 'labels' })}
                      </div>
                    )}

                    {form && (
                      <>
                        {/* Form name and description - identical to compilation mode */}
                        <div className="card mb-3">
                          <div className="card-body">
                            <h5 className="card-title">{form?.Name || ''}</h5>
                            <p className="card-text text-muted mb-0">{form?.Description || ''}</p>
                          </div>
                        </div>

                        {/* Progress Bar - identical to compilation mode */}
                        {renderProgressBar()}

                        <form key={formKey} ref={formRef} onSubmit={handleSubmit}>
                          {form.sections && form.sections.filter(section => isSectionVisible(section)).map((section, sIndex) => {
                            const sectionReadOnly = isSectionReadOnlyForUser(section.id);
                            return (
                              <FormSection
                                key={`sim-section-${section.id || sIndex}`}
                                section={section}
                                sIndex={sIndex}
                                formValues={formValues}
                                vocabularyItems={vocabularyItems}
                                customVocabularyItems={customVocabularyItems}
                                departmentsVoc={departmentsVoc}
                                functionalAreaVocs={functionalAreaVocs}
                                getDescendants={getDescendants}
                                answerFiles={answerFiles}
                                answerLinks={answerLinks}
                                answerObservations={answerObservations}
                                answerTexts={answerTexts}
                                activeAttachmentTab={activeAttachmentTab}
                                setActiveAttachmentTab={setActiveAttachmentTab}
                                handleInputChange={handleInputChange}
                                setFormValues={setFormValues}
                                setZoomImageUrl={setZoomImageUrl}
                                getLocalizedText={getLocalizedText}
                                getUrlFromFile={getUrlFromFile}
                                isImageFile={isImageFile}
                                isQuestionVisible={isQuestionVisible}
                                renderAttachmentArea={renderAttachmentArea}
                                renderFileUploadUI={renderFileUploadUI}
                                formatScore={formatScore}
                                colors={colors}
                                readOnly={effectiveReadOnly}
                                sectionReadOnly={sectionReadOnly}
                                isQuestionReadOnlyCollaborative={isQuestionReadOnlyCollaborative}
                                showScores={showScores}
                                validationErrors={validationErrors}
                                isCollapsed={!!collapsedSections[section.id || `s-${sIndex}`]}
                                onToggle={() => setCollapsedSections(prev => ({
                                  ...prev,
                                  [section.id || `s-${sIndex}`]: !prev[section.id || `s-${sIndex}`]
                                }))}
                                isSimulation={isSimulation}
                              />
                            );
                          })}

                          <div className="text-start mt-4">
                            {!effectiveReadOnly && (
                              <>
                                <button
                                  type="submit"
                                  className="btn me-2"
                                  style={{
                                    backgroundColor: colors.success,
                                    color: colors.white,
                                    borderColor: colors.success
                                  }}
                                >
                                  <FaPaperPlane className="me-1" /> {t('submitAnswers', { ns: 'labels' })}
                                </button>
                                <button
                                  type="button"
                                  className="btn"
                                  style={{
                                    backgroundColor: colors.secondary,
                                    color: colors.white,
                                    borderColor: colors.secondary
                                  }}
                                  onClick={handleClear}
                                >
                                  <FaEraser className="me-1" /> {t('clear', { ns: 'labels' })}
                                </button>
                              </>
                            )}
                          </div>
                        </form>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Side Panel Column - Show when guide or context data is available */}
              {/* Side Panel Column - Show when guide or context data is available AND not completely collapsed */}
              {(shouldShowUserGuideVisible() || (shouldShowContextData() && !isContextCollapsed)) && (
                <div className="col-xl-4 col-lg-5">
                  <div className="sticky-top" style={{ top: `${toolbarHeight || 100}px`, zIndex: 1020 }}>
                    {/* Context Data Display - Show when context data is available */}
                    {shouldShowContextData() && (
                      <div className="mb-3">
                        <ContextDataDisplay
                          contextData={formCompletion?.ContextData}
                          colors={colors}
                          isCollapsed={isContextCollapsed}
                          onToggleCollapse={toggleContextCollapse}
                          toolbarHeight={toolbarHeight || 100}
                        />
                      </div>
                    )}

                    {/* User Guide Display - Show when guide is available */}
                    {shouldShowUserGuideVisible() && (
                      <UserGuideDisplay
                        form={form}
                        currentUser={currentUser}
                        colors={colors}
                        isSimulation={isSimulation}
                        isCollapsed={isGuideCollapsed}
                        onToggleCollapse={toggleGuideCollapse}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  } else {
    // Compilation mode UI - similar to compilator.js
    return (
      <div className="d-flex flex-column" style={{ height: '100vh' }}>

        {/* 1) FIXED HEADER + TOOLBAR (never scrolls) - Hide in read-only mode */}
        {!effectiveReadOnly && (
          <header className="bg-white shadow-sm">
            <div className="container-fluid d-flex justify-content-between align-items-center py-2">

              {/* → breadcrumb navigation */}
              <ol className="breadcrumb mb-0"></ol>

              {/* → action icons: clear / save / submit / close */}
              <div className="d-flex gap-3">
                {/* Clear button icon */}
                <a
                  className="text-secondary fs-13"
                  onClick={handleClear}
                  role="button"
                  title={t('clear', { ns: 'labels', defaultValue: 'Clear' })}
                >
                  <i className="fa-solid fa-eraser fa-2x"></i>
                </a>

                {/* Save button icon - central position */}
                <a
                  className="text-primary fs-13"
                  onClick={saveFormCompletion}
                  role="button"
                  title={t('save', { ns: 'labels', defaultValue: 'Save' })}
                >
                  <i className="fa-solid fa-floppy-disk fa-2x"></i>
                </a>

                {/* Submit Answers / Complete Your Part button icon - only show for existing compilations */}
                {completionId && (() => {
                  // Check if this is a workflow with notifications and user is not the last actor
                  const isWorkflowNotLastActor = workflowInfo?.hasWorkflowNotifications && !workflowInfo?.isLastActor;

                  if (isWorkflowNotLastActor) {
                    // Show "Complete Your Part" button for non-last actors in workflow
                    return (
                      <a
                        className="fs-13"
                        onClick={handleCompleteYourPart}
                        role="button"
                        title={t('completeYourPart', { ns: 'labels', defaultValue: 'Completa la tua parte' })}
                        style={{ color: colors?.primary || '#f05f23' }}
                      >
                        <i className="fa-solid fa-check-circle fa-2x"></i>
                      </a>
                    );
                  } else {
                    // Show "Submit Answers" button for last actor or non-workflow forms
                    return (
                      <a
                        className="fs-13"
                        onClick={handleSubmitAnswers}
                        role="button"
                        title={t('submitAnswers', { ns: 'labels' })}
                        style={{ color: colors?.success || '#28a745' }}
                      >
                        <i className="fa-solid fa-paper-plane fa-2x"></i>
                      </a>
                    );
                  }
                })()}

                {/* Close button icon */}
                {onClose && (
                  <a
                    className="text-secondary fs-13"
                    onClick={onClose}
                    role="button"
                    title={t('close', { ns: 'labels', defaultValue: 'Close' })}
                  >
                    <i className="fa-solid fa-times fa-2x"></i>
                  </a>
                )}
              </div>
            </div>
          </header>
        )}

        {/* 2) SCROLLABLE CONTENT AREA */}
        <main className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
          <div className="container-fluid py-3">
            <div className="row">
              {/* Main form content - adjust width based on user guide presence */}
              <div className={(shouldShowUserGuideVisible() || (shouldShowContextData() && !isContextCollapsed)) ? "col-xl-8 col-lg-7" : "col-xl-10 offset-xl-1 col-lg-10 offset-lg-1"}>

                {/* Show guide availability indicator when guide is collapsed */}
                {shouldShowUserGuide() && isGuideCollapsed && (
                  <div className="alert alert-info d-flex justify-content-between align-items-center py-2 mb-3">
                    <div className="d-flex align-items-center">
                      <i className="fa-solid fa-book-open me-2"></i>
                      <span>{t('userGuideAvailable', { ns: 'labels', defaultValue: 'Guida utente disponibile' })}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={toggleGuideCollapse}
                    >
                      <i className="fa-solid fa-eye me-1"></i>
                      {t('showGuide', { ns: 'labels', defaultValue: 'Mostra Guida' })}
                    </button>
                  </div>
                )}

                {/* Show Context Data availability indicator when collapsed */}
                {shouldShowContextData() && isContextCollapsed && (
                  <div className="alert alert-light border d-flex justify-content-between align-items-center py-2 mb-3">
                    <div className="d-flex align-items-center text-secondary">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      <span>{t('contextDataAvailable', { ns: 'labels', defaultValue: 'Informazioni di contesto disponibili' })}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={toggleContextCollapse}
                    >
                      <i className="fa-solid fa-eye me-1"></i>
                      {t('showInfo', { ns: 'labels', defaultValue: 'Mostra Info' })}
                    </button>
                  </div>
                )}
                <div className="card h-auto">
                  <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: colors?.secondaryLight || '#e9ecef' }}>
                    <h5 className="mb-0">{t('formCompilation', { ns: 'labels', defaultValue: 'Form Compilation' })}</h5>
                    {/* Close button in read-only mode, styled like the Done button in section_editor */}
                    {effectiveReadOnly && onClose && (
                      <div>
                        <button
                          className="btn btn-sm ms-2"
                          style={{ backgroundColor: colors?.white || '#ffffff', color: colors?.dark || '#343a40' }}
                          onClick={onClose}
                        >
                          <FaCheck className="me-1" />
                          {t('done', { ns: 'labels' })}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="card-body p-3">
                    {/* Lock indicator for collaborative forms */}
                    {isCollaborative && lockState?.hasLock && !effectiveReadOnly && (
                      <div className="alert alert-success d-flex align-items-center mb-3">
                        <i className="fa-solid fa-lock-open me-2"></i>
                        <div>
                          <strong>{t('lockActive', { ns: 'labels', defaultValue: 'Stai modificando' })}</strong>
                          <br />
                          <small>{t('lockActiveMessage', { ns: 'labels', defaultValue: 'Hai il controllo esclusivo di questo form collaborativo' })}</small>
                        </div>
                      </div>
                    )}

                    {/* Mode-specific UI */}
                    {!effectiveReadOnly && (
                      <div className="alert alert-info">
                        <i className="fa-solid fa-info-circle me-2"></i>
                        {t('formCompilationInfo', { ns: 'labels', defaultValue: 'Complete the form and save your answers.' })}
                      </div>
                    )}

                    {effectiveReadOnly && (
                      <div className="alert alert-warning">
                        <i className="fa-solid fa-lock me-2"></i>
                        {t('readOnlyInfo', { ns: 'labels' })}
                      </div>
                    )}

                    {form && (
                      <>
                        {/* Form name and description */}
                        <div className="card mb-3">
                          <div className="card-body">
                            <h5 className="card-title">{form?.Name || ''}</h5>
                            <p className="card-text text-muted mb-0">{form?.Description || ''}</p>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        {renderProgressBar()}

                        <form key={formKey} ref={formRef} onSubmit={(e) => e.preventDefault()}>
                          {form.sections && form.sections.filter(section => isSectionVisible(section)).map((section, sIndex) => {
                            const sectionReadOnly = isSectionReadOnlyForUser(section.id);
                            return (
                              <div key={`comp-section-${section.id || sIndex}`} className={`card mb-4${sectionReadOnly ? ' border-secondary' : ''}`}>
                                {!section.hydeName && (
                                  <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: sectionReadOnly ? '#f8f9fa' : (colors?.secondaryLight || '#e9ecef') }}>
                                    <div>
                                      <h5 className="mb-0">
                                        {getLocalizedText(section.name, `${t('section', { ns: 'labels' })} ${sIndex + 1}`)}
                                        {sectionReadOnly && (
                                          <span
                                            className="badge bg-secondary ms-2"
                                            style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}
                                            title="Questa sezione è visibile in sola lettura"
                                          >
                                            <i className="fa-solid fa-eye me-1"></i>
                                            Sola lettura
                                          </span>
                                        )}
                                      </h5>
                                      {section.description && (
                                        <p className="text-muted mb-0 mt-1 small">
                                          {getLocalizedText(section.description, '')}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setCollapsedSections(prev => ({
                                        ...prev,
                                        [section.id || `s-${sIndex}`]: !prev[section.id || `s-${sIndex}`]
                                      }))}
                                      aria-expanded={!collapsedSections[section.id || `s-${sIndex}`]}
                                      aria-label={collapsedSections[section.id || `s-${sIndex}`] ? t('open', { ns: 'labels', defaultValue: 'Open' }) : t('close', { ns: 'labels', defaultValue: 'Close' })}
                                      title={collapsedSections[section.id || `s-${sIndex}`] ? t('open', { ns: 'labels', defaultValue: 'Open' }) : t('close', { ns: 'labels', defaultValue: 'Close' })}
                                    >
                                      <i className={`fa-solid ${collapsedSections[section.id || `s-${sIndex}`] ? 'fa-plus' : 'fa-minus'}`}></i>
                                    </button>
                                  </div>
                                )}
                                {!collapsedSections[section.id || `s-${sIndex}`] && (
                                  <div className="card-body">
                                    {section.questions && section.questions.map((question, qIndex) => {
                                      // Check visibility conditions
                                      if (!isQuestionVisible(question)) return null;

                                      const questionId = question.id || question.questionCode || `question-${sIndex}-${qIndex}`;
                                      const questionText = getLocalizedText(question.questionText, `${t('question', { ns: 'labels' })} ${qIndex + 1}`);
                                      const isRequired = question.mandatory === true;

                                      return (
                                        <div key={`comp-question-${questionId}`} className="mb-4 pb-3" style={{ borderBottom: qIndex < section.questions.length - 1 ? '1px solid #e9ecef' : 'none' }}>
                                          <div className="form-label fw-bold fs-5" style={{ display: 'block' }}>
                                            <div className="question-text-inline" style={{ display: 'inline' }}>
                                              <EditorJSRenderer
                                                content={getLocalizedText(question.questionText, '')}
                                                language={language}
                                                defaultText={`${t('question', { ns: 'labels' })} ${qIndex + 1}`}
                                              />
                                            </div>
                                            {isRequired && (
                                              <span className="text-danger" style={{ display: 'inline', marginLeft: '0.15rem' }}>*</span>
                                            )}
                                            {isQuestionReadOnlyCollaborative(questionId) && !sectionReadOnly && (
                                              <span
                                                className="badge bg-secondary ms-2"
                                                style={{ fontSize: '0.7rem', verticalAlign: 'middle', display: 'inline-block' }}
                                                title={t('questionAssignedToAnotherActor', { ns: 'labels', defaultValue: 'Questa domanda è assegnata ad un altro attore' })}
                                              >
                                                <i className="fa-solid fa-lock me-1"></i>
                                                {t('assignedToOther', { ns: 'labels', defaultValue: 'Assegnata ad altro attore' })}
                                              </span>
                                            )}
                                          </div>

                                          {/* Question description if present */}
                                          {question.description && (
                                            <div className="text-muted small mb-2">
                                              <EditorJSRenderer
                                                content={getLocalizedText(question.description, '')}
                                                language={language}
                                                defaultText=""
                                              />
                                            </div>
                                          )}

                                          {/* Display attached files if any - for any question type */}
                                          {question.files && question.files.length > 0 && (
                                            <div className="mt-2 mb-3">
                                              <ul className="list-group list-group-flush">
                                                {question.files.map((file, fIndex) => {
                                                  const fileData = getUrlFromFile(file);
                                                  return (
                                                    <li key={`file-${questionId}-${file.Id}`} className="list-group-item py-2 px-2">
                                                      {isImageFile(fileData.mimeType) ? (
                                                        <div className="d-flex justify-content-center">
                                                          <div
                                                            className="card shadow-sm border rounded-3"
                                                            style={{ maxWidth: "100%", maxHeight: "320px" }}
                                                          >
                                                            <div className="p-2">
                                                              <AuthenticatedImage
                                                                file={file}
                                                                alt={file.Name || `Image ${fIndex + 1}`}
                                                                className="img-fluid rounded"
                                                                onClick={async () => {
                                                                  // Load authenticated image for zoom
                                                                  const imageUrl = await loadAuthenticatedImage(file);
                                                                  if (imageUrl) {
                                                                    setZoomImageUrl(imageUrl);
                                                                  }
                                                                }}
                                                                style={{
                                                                  maxHeight: "300px",
                                                                  objectFit: "contain",
                                                                  width: "100%",
                                                                  cursor: "zoom-in",
                                                                }}
                                                              />
                                                            </div>
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <div className="d-flex justify-content-between align-items-center">
                                                          <div>
                                                            <i className="fa-solid fa-paperclip me-2"></i>
                                                            {file.Name || `File ${fIndex + 1}`}
                                                            <span className="text-muted ms-2 small">
                                                              ({Math.round(file.Size / 1024)} KB)
                                                            </span>
                                                          </div>
                                                          <div className="btn-group">
                                                            <button
                                                              type="button"
                                                              className="btn btn-sm btn-outline-primary"
                                                              onClick={() => handleFileOpen(file)}
                                                              title={t("openFile", { ns: "labels", defaultValue: "Open File" })}
                                                            >
                                                              <i className="fa-solid fa-eye"></i> {t("open", { ns: "labels", defaultValue: "Open" })}
                                                            </button>
                                                            <button
                                                              type="button"
                                                              className="btn btn-sm btn-outline-secondary"
                                                              onClick={() => handleFileDownload(file)}
                                                              title={t("downloadFile", { ns: "labels", defaultValue: "Download File" })}
                                                            >
                                                              <FaDownload /> {t("download", { ns: "labels" })}
                                                            </button>
                                                          </div>
                                                        </div>
                                                      )}
                                                    </li>
                                                  );
                                                })}
                                              </ul>

                                              {/* 🔍 Modal per zoom immagine */}
                                              {zoomImageUrl && (
                                                <div
                                                  className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                  style={{
                                                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                                                    zIndex: 1050,
                                                  }}
                                                  onClick={() => setZoomImageUrl(null)}
                                                >
                                                  <div
                                                    className="position-relative"
                                                    onClick={(e) => e.stopPropagation()}
                                                  >
                                                    <button
                                                      type="button"
                                                      className="btn-close btn-close-white position-absolute end-0 top-0"
                                                      style={{ zIndex: 1060, margin: "10px" }}
                                                      onClick={() => setZoomImageUrl(null)}
                                                    ></button>
                                                    <img
                                                      src={zoomImageUrl}
                                                      alt="Zoomed"
                                                      className="img-fluid rounded shadow"
                                                      style={{
                                                        maxHeight: "90vh",
                                                        maxWidth: "95vw",
                                                        objectFit: "contain",
                                                        border: "6px solid white",
                                                      }}
                                                    />
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )}

                                          {/* Validation Rules - Show for multiple choice questions only (not in simulation mode) */}
                                          {!isSimulation && (
                                            <QuestionValidationRules
                                              question={question}
                                              colors={colors}
                                            />
                                          )}

                                          {/* Open type questions (textarea) */}
                                          {question.questionType === 'open' && (
                                            <textarea
                                              className="form-control mt-3"
                                              rows="3"
                                              placeholder={t('enterAnswer', { ns: 'labels' })}
                                              required={isRequired}
                                              disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                              value={formValues[questionId] || ''}
                                              onChange={(e) => handleInputChange(questionId, e.target.value)}
                                            ></textarea>
                                          )}

                                          {/* Open Number type questions (input with number validation) */}
                                          {question.questionType === 'openNumber' && (
                                            <input
                                              type="text"
                                              className="form-control mt-3"
                                              placeholder={t('enterNumberAnswer', { ns: 'labels' })}
                                              required={isRequired}
                                              disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                              value={formValues[questionId] || ''}
                                              onChange={(e) => {
                                                // Allow only numbers (including leading zeros)
                                                const value = e.target.value;
                                                if (value === '' || /^\d+$/.test(value)) {
                                                  handleInputChange(questionId, value);
                                                }
                                              }}
                                              onKeyPress={(e) => {
                                                // Prevent non-numeric characters from being typed
                                                if (!/\d/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
                                                  e.preventDefault();
                                                }
                                              }}
                                            />
                                          )}

                                          {/* Date question type */}
                                          {question.questionType === 'date' && (
                                            <input
                                              type="date"
                                              className="form-control mt-3"
                                              placeholder={t('selectDate', { ns: 'labels' })}
                                              required={isRequired}
                                              disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                              value={formValues[questionId] || ''}
                                              onChange={(e) => handleInputChange(questionId, e.target.value)}
                                            />
                                          )}

                                          {/* Time question type */}
                                          {question.questionType === 'time' && (
                                            <input
                                              type="time"
                                              className="form-control mt-3"
                                              placeholder={t('selectTime', { ns: 'labels' })}
                                              required={isRequired}
                                              disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                              value={formValues[questionId] || ''}
                                              onChange={(e) => handleInputChange(questionId, e.target.value)}
                                            />
                                          )}

                                          {/* DateTime question type */}
                                          {question.questionType === 'datetime' && (
                                            <input
                                              type="datetime-local"
                                              className="form-control mt-3"
                                              placeholder={t('selectDateTime', { ns: 'labels' })}
                                              required={isRequired}
                                              disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                              value={formValues[questionId] || ''}
                                              onChange={(e) => handleInputChange(questionId, e.target.value)}
                                            />
                                          )}

                                          {/* Boolean questions (yes/no radio buttons) */}
                                          {(question.questionType === 'boolean' || question.questionType === 'trueFalse') && question.answers && (
                                            <div className="mt-3">
                                              {question.answers.filter(a => !a.invisible).map((answer, aIndex) => (
                                                <div key={`comp-radio-${questionId}-${answer.answerId || answer.id}`} className="form-check">
                                                  <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name={questionId}
                                                    id={`radio-${questionId}-${answer.answerId || answer.id}`}
                                                    required={isRequired}
                                                    disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                    value={answer.answerId || answer.id}
                                                    checked={String(formValues[questionId]) === String(answer.answerId || answer.id)}
                                                    onChange={(e) => handleInputChange(questionId, e.target.value)}
                                                  />
                                                  <label className="form-check-label" htmlFor={`radio-${questionId}-${answer.answerId || answer.id}`}>
                                                    {getLocalizedText(answer.answerText, `${t('option', { ns: 'labels' })} ${aIndex + 1}`)}
                                                    {showScores && formatScore(answer.score) && (
                                                      <span style={{ color: 'black', marginLeft: '10px' }}>({t('score', { ns: 'labels' })}: {formatScore(answer.score)})</span>
                                                    )}
                                                  </label>
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {/* Yes/No/NA questions (three radio buttons) */}
                                          {question.questionType === 'yesNoNA' && question.answers && (
                                            <div className="mt-3">
                                              {question.answers.filter(a => !a.invisible).map((answer, aIndex) => (
                                                <div key={`comp-radio-${questionId}-${answer.answerId || answer.id}`} className="form-check">
                                                  <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name={questionId}
                                                    id={`radio-${questionId}-${answer.answerId || answer.id}`}
                                                    required={isRequired}
                                                    disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                    value={answer.answerId || answer.id}
                                                    checked={String(formValues[questionId]) === String(answer.answerId || answer.id)}
                                                    onChange={(e) => handleInputChange(questionId, e.target.value)}
                                                  />
                                                  <label className="form-check-label" htmlFor={`radio-${questionId}-${answer.answerId || answer.id}`}>
                                                    {getLocalizedText(answer.answerText, `${t('option', { ns: 'labels' })} ${aIndex + 1}`)}
                                                    {showScores && formatScore(answer.score) && (
                                                      <span style={{ color: 'black', marginLeft: '10px' }}>({t('score', { ns: 'labels' })}: {formatScore(answer.score)})</span>
                                                    )}
                                                  </label>
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {/* PDCA questions (checkboxes) */}
                                          {question.questionType === 'pdca' && question.answers && (() => {
                                            // Calculate if there's at least one editable answer (to show icon only for partial assignments)
                                            const visibleAnswers = question.answers.filter(a => !a.invisible);
                                            const hasAtLeastOneEditableAnswer = visibleAnswers.some(answer =>
                                              !isAnswerReadOnlyCollaborative(questionId, answer.answerId || answer.id)
                                            );

                                            return (
                                              <div className="mt-3">
                                                {visibleAnswers.map((answer, aIndex) => {
                                                  // Check if this answer is selected in the current values
                                                  const isChecked = Array.isArray(formValues[questionId]) &&
                                                    formValues[questionId].includes(answer.answerId || answer.id);

                                                  // Get disabled answers based on validation rules
                                                  const disabledAnswers = getDisabledAnswers(question, formValues[questionId] || []);
                                                  const isDisabledByValidation = disabledAnswers.includes(answer.answerId || answer.id);

                                                  // Check if this specific answer is readonly in collaborative mode
                                                  const isAnswerReadOnly = isAnswerReadOnlyCollaborative(questionId, answer.answerId || answer.id);

                                                  return (
                                                    <div key={`comp-checkbox-${questionId}-${answer.answerId || answer.id}`} className="form-check">
                                                      <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`checkbox-${questionId}-${answer.answerId || answer.id}`}
                                                        name={`${questionId}[]`}
                                                        value={answer.answerId || answer.id}
                                                        checked={isChecked}
                                                        disabled={effectiveReadOnly || isAnswerReadOnly || isDisabledByValidation}
                                                        onChange={(e) => {
                                                          // For checkboxes, we need to handle arrays of values
                                                          const currentValues = formValues[questionId] || [];
                                                          const value = answer.answerId || answer.id;

                                                          if (e.target.checked) {
                                                            // Add value if not already in array
                                                            if (!currentValues.includes(value)) {
                                                              setFormValues(prev => ({
                                                                ...prev,
                                                                [questionId]: [...currentValues, value]
                                                              }));
                                                            }
                                                          } else {
                                                            // Remove value from array
                                                            setFormValues(prev => ({
                                                              ...prev,
                                                              [questionId]: currentValues.filter(v => v !== value)
                                                            }));
                                                          }
                                                        }}
                                                      />
                                                      <label
                                                        className={`form-check-label ${(isDisabledByValidation || isAnswerReadOnly) ? 'text-muted' : ''}`}
                                                        htmlFor={`checkbox-${questionId}-${answer.answerId || answer.id}`}
                                                        style={{ opacity: (isDisabledByValidation || isAnswerReadOnly) ? 0.6 : 1 }}
                                                        title={
                                                          isDisabledByValidation
                                                            ? t('optionDisabledByValidation', { ns: 'labels', defaultValue: 'Opzione disabilitata dalle regole di validazione' })
                                                            : isAnswerReadOnly
                                                              ? t('optionNotAssignedToYou', { ns: 'labels', defaultValue: 'Opzione non assegnata a te' })
                                                              : ''
                                                        }
                                                      >
                                                        {getLocalizedText(answer.answerText, `${t('option', { ns: 'labels' })} ${aIndex + 1}`)}
                                                        {isDisabledByValidation && (
                                                          <i className="fa-solid fa-lock ms-1 text-muted small"></i>
                                                        )}
                                                        {/* Show icon only if there's at least one editable answer (partial assignment) */}
                                                        {isAnswerReadOnly && hasAtLeastOneEditableAnswer && (
                                                          <i className="fa-solid fa-user-lock ms-1 text-muted small" title={t('answerAssignedToOtherActor', { ns: 'labels', defaultValue: 'Risposta assegnata ad altro attore' })}></i>
                                                        )}
                                                        {showScores && formatScore(answer.score) && (
                                                          <span style={{ color: 'black', marginLeft: '10px' }}>({t('score', { ns: 'labels' })}: {formatScore(answer.score)})</span>
                                                        )}
                                                      </label>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            );
                                          })()}

                                          {/* Single selection questions (radio buttons) */}
                                          {question.questionType === 'single' && question.answers && (
                                            <div className="mt-3">
                                              {question.answers.filter(a => !a.invisible).map((answer, aIndex) => (
                                                <div key={`comp-radio-${questionId}-${answer.answerId || answer.id}`} className="form-check">
                                                  <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name={questionId}
                                                    id={`radio-${questionId}-${answer.answerId || answer.id}`}
                                                    required={isRequired}
                                                    disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                    value={answer.answerId || answer.id}
                                                    checked={String(formValues[questionId]) === String(answer.answerId || answer.id)}
                                                    onChange={(e) => handleInputChange(questionId, e.target.value)}
                                                  />
                                                  <label className="form-check-label" htmlFor={`radio-${questionId}-${answer.answerId || answer.id}`}>
                                                    {getLocalizedText(answer.answerText, `${t('option', { ns: 'labels' })} ${aIndex + 1}`)}
                                                    {showScores && formatScore(answer.score) && (
                                                      <span style={{ color: 'black', marginLeft: '10px' }}>({t('score', { ns: 'labels' })}: {formatScore(answer.score)})</span>
                                                    )}
                                                  </label>
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {/* Multiple selection questions (checkboxes) */}
                                          {question.questionType === 'multiple' && question.answers && (() => {
                                            // Calculate if there's at least one editable answer (to show icon only for partial assignments)
                                            const visibleAnswers = question.answers.filter(a => !a.invisible);
                                            const hasAtLeastOneEditableAnswer = visibleAnswers.some(answer =>
                                              !isAnswerReadOnlyCollaborative(questionId, answer.answerId || answer.id)
                                            );

                                            return (
                                              <div className="mt-3">
                                                {visibleAnswers.map((answer, aIndex) => {
                                                  // Check if this answer is selected in the current values
                                                  const isChecked = Array.isArray(formValues[questionId]) &&
                                                    formValues[questionId].includes(answer.answerId || answer.id);

                                                  // Get disabled answers based on validation rules
                                                  const disabledAnswers = getDisabledAnswers(question, formValues[questionId] || []);
                                                  const isDisabledByValidation = disabledAnswers.includes(answer.answerId || answer.id);

                                                  // Check if this specific answer is readonly in collaborative mode
                                                  const isAnswerReadOnly = isAnswerReadOnlyCollaborative(questionId, answer.answerId || answer.id);

                                                  return (
                                                    <div key={`comp-checkbox-${questionId}-${answer.answerId || answer.id}`} className="form-check">
                                                      <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`checkbox-${questionId}-${answer.answerId || answer.id}`}
                                                        name={`${questionId}[]`}
                                                        value={answer.answerId || answer.id}
                                                        checked={isChecked}
                                                        disabled={effectiveReadOnly || isAnswerReadOnly || isDisabledByValidation}
                                                        onChange={(e) => {
                                                          // For checkboxes, we need to handle arrays of values
                                                          const currentValues = formValues[questionId] || [];
                                                          const value = answer.answerId || answer.id;

                                                          if (e.target.checked) {
                                                            // Add value if not already in array
                                                            if (!currentValues.includes(value)) {
                                                              setFormValues(prev => ({
                                                                ...prev,
                                                                [questionId]: [...currentValues, value]
                                                              }));
                                                            }
                                                          } else {
                                                            // Remove value from array
                                                            setFormValues(prev => ({
                                                              ...prev,
                                                              [questionId]: currentValues.filter(v => v !== value)
                                                            }));
                                                          }
                                                        }}
                                                      />
                                                      <label
                                                        className={`form-check-label ${(isDisabledByValidation || isAnswerReadOnly) ? 'text-muted' : ''}`}
                                                        htmlFor={`checkbox-${questionId}-${answer.answerId || answer.id}`}
                                                        style={{ opacity: (isDisabledByValidation || isAnswerReadOnly) ? 0.6 : 1 }}
                                                        title={
                                                          isDisabledByValidation
                                                            ? t('optionDisabledByValidation', { ns: 'labels', defaultValue: 'Opzione disabilitata dalle regole di validazione' })
                                                            : isAnswerReadOnly
                                                              ? t('optionNotAssignedToYou', { ns: 'labels', defaultValue: 'Opzione non assegnata a te' })
                                                              : ''
                                                        }
                                                      >
                                                        {getLocalizedText(answer.answerText, `${t('option', { ns: 'labels' })} ${aIndex + 1}`)}
                                                        {isDisabledByValidation && (
                                                          <i className="fa-solid fa-lock ms-1 text-muted small"></i>
                                                        )}
                                                        {/* Show icon only if there's at least one editable answer (partial assignment) */}
                                                        {isAnswerReadOnly && hasAtLeastOneEditableAnswer && (
                                                          <i className="fa-solid fa-user-lock ms-1 text-muted small" title={t('answerAssignedToOtherActor', { ns: 'labels', defaultValue: 'Risposta assegnata ad altro attore' })}></i>
                                                        )}
                                                        {showScores && formatScore(answer.score) && (
                                                          <span style={{ color: 'black', marginLeft: '10px' }}>({t('score', { ns: 'labels' })}: {formatScore(answer.score)})</span>
                                                        )}
                                                      </label>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            );
                                          })()}

                                          {/* Display validation errors for multiple choice questions */}
                                          {(question.questionType === 'multiple' || question.questionType === 'pdca') &&
                                            validationErrors[questionId] && validationErrors[questionId].length > 0 && (
                                              <ValidationErrorDisplay
                                                errors={validationErrors[questionId]}
                                                colors={colors}
                                              />
                                            )}

                                          {/* Vocabulary questions (dropdown with multiple selection) */}
                                          {question.questionType === 'vocabulary' && question.selectedVocabulary && (
                                            <div className="mt-3">
                                              {/* Dropdown per aggiungere vocabolari */}
                                              {!effectiveReadOnly && !isQuestionReadOnlyCollaborative(questionId) && (
                                                <div className="mb-3">
                                                  <select
                                                    className="form-select"
                                                    onChange={(e) => {
                                                      if (e.target.value) {
                                                        const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                                                        if (!currentValues.includes(e.target.value)) {
                                                          handleInputChange(questionId, [...currentValues, e.target.value]);
                                                        }
                                                        e.target.value = ""; // Reset selection
                                                      }
                                                    }}
                                                  >
                                                    <option value="">{t('selectVocabulary', { ns: 'labels' })}</option>
                                                    {vocabularyItems[question.selectedVocabulary] && vocabularyItems[question.selectedVocabulary]
                                                      .filter(item => {
                                                        const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                                                        return !currentValues.includes(item.Code);
                                                      })
                                                      .map(item => (
                                                        <option key={item.Code} value={item.Code}>
                                                          {item.Name}
                                                        </option>
                                                      ))}
                                                  </select>
                                                </div>
                                              )}

                                              {/* Lista degli elementi selezionati */}
                                              {(() => {
                                                const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                                                if (currentValues.length > 0) {
                                                  return (
                                                    <div className="list-group">
                                                      {currentValues.map((code) => {
                                                        const vocItem = vocabularyItems[question.selectedVocabulary]?.find(item => item.Code === code);
                                                        if (!vocItem) return null;

                                                        return (
                                                          <div key={code} className="list-group-item d-flex justify-content-between align-items-center">
                                                            <div className="d-flex align-items-center">
                                                              <i className="fas fa-bookmark text-primary me-3 fs-5"></i>
                                                              <div>
                                                                <h6 className="fs-14 mb-0 fw-semibold">{vocItem.Name}</h6>
                                                              </div>
                                                            </div>
                                                            {!effectiveReadOnly && !isQuestionReadOnlyCollaborative(questionId) && (
                                                              <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => {
                                                                  const updatedValues = currentValues.filter(v => v !== code);
                                                                  handleInputChange(questionId, updatedValues);
                                                                }}
                                                                title={t('remove', { ns: 'labels', defaultValue: 'Remove' })}
                                                              >
                                                                <i className="fas fa-times"></i>
                                                              </button>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  );
                                                } else {
                                                  return (
                                                    <div className="alert alert-info">
                                                      <p className="mb-0">{t('noVocabularySelected', { ns: 'labels', defaultValue: 'Nessun elemento selezionato' })}</p>
                                                    </div>
                                                  );
                                                }
                                              })()}
                                            </div>
                                          )}

                                          {/* Custom Vocabulary questions (dropdown with multiple selection) */}
                                          {question.questionType === 'customVocabulary' && question.selectedCustomVocabulary && (
                                            <div className="mt-3">
                                              {/* Dropdown per aggiungere vocabolari personalizzati */}
                                              {!effectiveReadOnly && (
                                                <div className="mb-3">
                                                  <select
                                                    className="form-select"
                                                    onChange={(e) => {
                                                      if (e.target.value) {
                                                        const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                                                        if (!currentValues.includes(e.target.value)) {
                                                          handleInputChange(questionId, [...currentValues, e.target.value]);
                                                        }
                                                        e.target.value = ""; // Reset selection
                                                      }
                                                    }}
                                                  >
                                                    <option value="">{t('selectCustomVocabulary', { ns: 'labels' }) || 'Seleziona Vocabolario Personalizzato'}</option>
                                                    {customVocabularyItems[question.selectedCustomVocabulary] && customVocabularyItems[question.selectedCustomVocabulary]
                                                      .filter(item => {
                                                        const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                                                        return !currentValues.includes(item.Code);
                                                      })
                                                      .map(item => (
                                                        <option key={item.Code} value={item.Code}>
                                                          {item.Name}
                                                        </option>
                                                      ))}
                                                  </select>
                                                </div>
                                              )}

                                              {/* Lista degli elementi selezionati */}
                                              {(() => {
                                                const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                                                if (currentValues.length > 0) {
                                                  return (
                                                    <div className="list-group">
                                                      {currentValues.map((code) => {
                                                        const vocItem = customVocabularyItems[question.selectedCustomVocabulary]?.find(item => item.Code === code);
                                                        if (!vocItem) return null;

                                                        return (
                                                          <div key={code} className="list-group-item d-flex justify-content-between align-items-center">
                                                            <div className="d-flex align-items-center">
                                                              <i className="fas fa-list text-primary me-3 fs-5"></i>
                                                              <div>
                                                                <h6 className="fs-14 mb-0 fw-semibold">{vocItem.Name}</h6>
                                                              </div>
                                                            </div>
                                                            {!effectiveReadOnly && !isQuestionReadOnlyCollaborative(questionId) && (
                                                              <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => {
                                                                  const updatedValues = currentValues.filter(v => v !== code);
                                                                  handleInputChange(questionId, updatedValues);
                                                                }}
                                                                title={t('remove', { ns: 'labels', defaultValue: 'Remove' })}
                                                              >
                                                                <i className="fas fa-times"></i>
                                                              </button>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  );
                                                } else {
                                                  return (
                                                    <div className="alert alert-info">
                                                      <p className="mb-0">{t('noCustomVocabularySelected', { ns: 'labels', defaultValue: 'Nessun vocabolario personalizzato selezionato' })}</p>
                                                    </div>
                                                  );
                                                }
                                              })()}
                                            </div>
                                          )}

                                          {/* Department/Functional Area questions (two dropdowns) */}
                                          {question.questionType === 'departmentFunctionalArea' && (
                                            <div className="mt-3">
                                              <div className="row">
                                                <div className="col-md-6 mb-3">
                                                  <label className="form-label fw-bold">{t('department', { ns: 'labels' })}</label>
                                                  <Choices
                                                    voc={departmentsVoc || []}
                                                    value="Id"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={(() => {
                                                      const currentValue = formValues[questionId];
                                                      if (currentValue && currentValue.includes('|')) {
                                                        const departmentId = currentValue.split('|')[0];
                                                        const dept = departmentsVoc.find(d => d.Id === departmentId);
                                                        return dept ? [dept] : [];
                                                      }
                                                      return [];
                                                    })()}
                                                    onChange={(e) => {
                                                      const selectedDepartmentId = e.selected?.[0]?.Id || "";

                                                      // When department changes, clear the functional area selection
                                                      // This forces the user to explicitly select a functional area
                                                      const newValue = selectedDepartmentId ? `${selectedDepartmentId}|` : "";
                                                      handleInputChange(questionId, newValue);
                                                    }}
                                                    placeholder={t('selectDepartment', { ns: 'labels' })}
                                                    dropDownParent="root"
                                                    allowClear={!isRequired}
                                                    disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                  />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                  <label className="form-label fw-bold">{t('functionalArea', { ns: 'labels' })}</label>
                                                  <Choices
                                                    voc={(() => {
                                                      const currentValue = formValues[questionId];
                                                      if (currentValue && currentValue.includes('|')) {
                                                        const departmentId = currentValue.split('|')[0];
                                                        if (departmentId) {
                                                          return getDescendants(functionalAreaVocs, departmentId);
                                                        }
                                                      }
                                                      return [];
                                                    })()}
                                                    value="Id"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={(() => {
                                                      const currentValue = formValues[questionId];
                                                      if (currentValue && currentValue.includes('|')) {
                                                        const functionalAreaId = currentValue.split('|')[1];
                                                        const area = functionalAreaVocs.find(a => a.Id === functionalAreaId);
                                                        return area ? [area] : [];
                                                      }
                                                      return [];
                                                    })()}
                                                    onChange={(e) => {
                                                      const selectedFunctionalAreaId = e.selected?.[0]?.Id || "";
                                                      const currentValue = formValues[questionId] || "";
                                                      const departmentId = currentValue.includes('|') ? currentValue.split('|')[0] : "";

                                                      // Update the combined value
                                                      const newValue = departmentId ? `${departmentId}|${selectedFunctionalAreaId}` : "";
                                                      handleInputChange(questionId, newValue);
                                                    }}
                                                    placeholder={t('selectFunctionalArea', { ns: 'labels' })}
                                                    dropDownParent="root"
                                                    allowClear={true}
                                                    disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          )}

                                          {/* Table questions */}
                                          {question.questionType === 'table' && (() => {
                                            const columns = question.tableColumns || [];
                                            const tableSettings = question.tableSettings || {};
                                            const currentTableData = formValues[questionId] || [];

                                            if (columns.length === 0) {
                                              return (
                                                <div className="alert alert-warning mt-3">
                                                  <i className="fa fa-exclamation-triangle me-2"></i>
                                                  {t('tableNoColumns', { ns: 'labels', defaultValue: 'Nessuna colonna configurata per questa tabella' })}
                                                </div>
                                              );
                                            }

                                            // Helper function to get localized column name
                                            const getColumnName = (column) => {
                                              return getLocalizedText(column.name, `Column ${columns.indexOf(column) + 1}`);
                                            };

                                            // Helper function to add a new row
                                            const addRow = () => {
                                              if (effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)) return;

                                              const newRow = {};
                                              columns.forEach(col => {
                                                newRow[col.id] = '';
                                              });

                                              const updatedData = [...currentTableData, newRow];
                                              handleInputChange(questionId, updatedData);
                                            };

                                            // Helper function to delete a row
                                            const deleteRow = (rowIndex) => {
                                              if (effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)) return;

                                              const updatedData = [...currentTableData];
                                              updatedData.splice(rowIndex, 1);
                                              handleInputChange(questionId, updatedData);
                                            };

                                            // Helper function to update cell value
                                            const updateCell = (rowIndex, columnId, value) => {
                                              if (effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)) return;

                                              const updatedData = [...currentTableData];
                                              if (!updatedData[rowIndex]) {
                                                updatedData[rowIndex] = {};
                                              }
                                              updatedData[rowIndex][columnId] = value;
                                              handleInputChange(questionId, updatedData);
                                            };

                                            // Render table cell based on column type
                                            const renderTableCell = (row, column, rowIndex) => {
                                              const cellValue = row[column.id] || '';
                                              const cellId = `${questionId}_${rowIndex}_${column.id}`;

                                              switch (column.type) {
                                                case 'text':
                                                  return (
                                                    <input
                                                      type="text"
                                                      className="form-control form-control-sm"
                                                      placeholder={column.placeholder || ''}
                                                      value={cellValue}
                                                      disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                      onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
                                                      required={column.required}
                                                      minLength={column.validation?.minLength}
                                                      maxLength={column.validation?.maxLength}
                                                    />
                                                  );
                                                case 'number':
                                                  return (
                                                    <input
                                                      type="number"
                                                      className="form-control form-control-sm"
                                                      placeholder={column.placeholder || ''}
                                                      value={cellValue}
                                                      disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                      onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
                                                      required={column.required}
                                                      min={column.validation?.min}
                                                      max={column.validation?.max}
                                                    />
                                                  );
                                                case 'date':
                                                  return (
                                                    <input
                                                      type="date"
                                                      className="form-control form-control-sm"
                                                      value={cellValue}
                                                      disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                      onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
                                                      required={column.required}
                                                    />
                                                  );
                                                case 'time':
                                                  return (
                                                    <input
                                                      type="time"
                                                      className="form-control form-control-sm"
                                                      value={cellValue}
                                                      disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                      onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
                                                      required={column.required}
                                                    />
                                                  );
                                                case 'datetime':
                                                  return (
                                                    <input
                                                      type="datetime-local"
                                                      className="form-control form-control-sm"
                                                      value={cellValue}
                                                      disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                      onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
                                                      required={column.required}
                                                    />
                                                  );
                                                case 'select':
                                                  return (
                                                    <select
                                                      className="form-select form-select-sm"
                                                      value={cellValue}
                                                      disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                      onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
                                                      required={column.required}
                                                    >
                                                      <option value="">{t('select', { ns: 'labels', defaultValue: 'Seleziona...' })}</option>
                                                      {column.options?.map((option, optIndex) => (
                                                        <option key={optIndex} value={option.value}>
                                                          {getLocalizedText(option.text, option.value)}
                                                        </option>
                                                      ))}
                                                    </select>
                                                  );
                                                case 'customVocabulary':
                                                  return (
                                                    <Choices
                                                      voc={(() => {
                                                        const selectedId = column.selectedCustomVocabulary;
                                                        return selectedId ? (customVocabularyItems[selectedId] || []) : [];
                                                      })()}
                                                      value="Code"
                                                      text="Name"
                                                      className="inputStyle blackText bg-white"
                                                      selected={cellValue ? [{ Code: cellValue }] : []}
                                                      onChange={(e) => {
                                                        const selectedValue = e.selected?.[0]?.Code || "";
                                                        updateCell(rowIndex, column.id, selectedValue);
                                                      }}
                                                      placeholder={t('selectItem', { ns: 'labels', defaultValue: 'Seleziona elemento' })}
                                                      dropDownParent="root"
                                                      allowClear={!column.required}
                                                      disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                    />
                                                  );
                                                case 'period': {
                                                  const includeTime = !!column.periodIncludeTime;
                                                  const [startVal, endVal] = (() => {
                                                    if (typeof cellValue === 'string' && cellValue.includes('|')) {
                                                      const [s, e] = cellValue.split('|');
                                                      return [s || '', e || ''];
                                                    }
                                                    return ['', ''];
                                                  })();

                                                  const inputType = includeTime ? 'datetime-local' : 'date';

                                                  const toComparable = (val) => {
                                                    if (!val) return null;
                                                    try {
                                                      return new Date(val).getTime();
                                                    } catch (e) {
                                                      return null;
                                                    }
                                                  };

                                                  const updatePeriod = (nextStart, nextEnd) => {
                                                    const s = toComparable(nextStart);
                                                    const e = toComparable(nextEnd);
                                                    if (s !== null && e !== null && s > e) {
                                                      const fixed = `${nextStart}|${nextStart}`;
                                                      updateCell(rowIndex, column.id, fixed);
                                                      return;
                                                    }
                                                    updateCell(rowIndex, column.id, `${nextStart || ''}|${nextEnd || ''}`);
                                                  };

                                                  return (
                                                    <div className="d-flex gap-2 align-items-center">
                                                      <input
                                                        type={inputType}
                                                        className="form-control form-control-sm"
                                                        value={startVal}
                                                        disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                        onChange={(e) => updatePeriod(e.target.value, endVal)}
                                                        required={column.required}
                                                      />
                                                      <span className="text-muted small">{t('to', { ns: 'labels', defaultValue: 'a' })}</span>
                                                      <input
                                                        type={inputType}
                                                        className="form-control form-control-sm"
                                                        value={endVal}
                                                        disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                        onChange={(e) => updatePeriod(startVal, e.target.value)}
                                                        required={column.required}
                                                        min={!includeTime ? (startVal || undefined) : undefined}
                                                      />
                                                    </div>
                                                  );
                                                }
                                                default:
                                                  return (
                                                    <input
                                                      type="text"
                                                      className="form-control form-control-sm"
                                                      placeholder={column.placeholder || ''}
                                                      value={cellValue}
                                                      disabled={effectiveReadOnly || isQuestionReadOnlyCollaborative(questionId)}
                                                      onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
                                                      required={column.required}
                                                    />
                                                  );
                                              }
                                            };

                                            return (
                                              <div className="table-responsive mt-3">
                                                <table className="table table-bordered table-sm">
                                                  <thead>
                                                    <tr>
                                                      {tableSettings.showRowNumbers && (
                                                        <th style={{ width: '60px' }}>{t('rowNumber', { ns: 'labels', defaultValue: 'N.' })}</th>
                                                      )}
                                                      {columns.map((column) => (
                                                        <th key={column.id}>
                                                          {getColumnName(column)}
                                                          {column.required && <span className="text-danger ms-1">*</span>}
                                                        </th>
                                                      ))}
                                                      {tableSettings.allowDeleteRows && !effectiveReadOnly && !isQuestionReadOnlyCollaborative(questionId) && (
                                                        <th style={{ width: '80px' }}>{t('tableAction', { ns: 'labels', defaultValue: 'Azione' })}</th>
                                                      )}
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {currentTableData.length === 0 ? (
                                                      <tr>
                                                        <td colSpan={columns.length + (tableSettings.showRowNumbers ? 1 : 0) + (tableSettings.allowDeleteRows && !effectiveReadOnly && !isQuestionReadOnlyCollaborative(questionId) ? 1 : 0)} className="text-center text-muted">
                                                          {t('tableEmpty', { ns: 'labels', defaultValue: 'Nessun dato nella tabella' })}
                                                        </td>
                                                      </tr>
                                                    ) : (
                                                      currentTableData.map((row, rowIndex) => (
                                                        <tr key={rowIndex}>
                                                          {tableSettings.showRowNumbers && (
                                                            <td className="text-center">{rowIndex + 1}</td>
                                                          )}
                                                          {columns.map((column) => (
                                                            <td key={column.id}>
                                                              {renderTableCell(row, column, rowIndex)}
                                                            </td>
                                                          ))}
                                                          {tableSettings.allowDeleteRows && !effectiveReadOnly && !isQuestionReadOnlyCollaborative(questionId) && (
                                                            <td className="text-center">
                                                              <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => deleteRow(rowIndex)}
                                                                disabled={currentTableData.length <= (tableSettings.minRows || 0)}
                                                              >
                                                                <i className="fa fa-trash"></i>
                                                              </button>
                                                            </td>
                                                          )}
                                                        </tr>
                                                      ))
                                                    )}
                                                  </tbody>
                                                </table>

                                                {/* Add Row Button */}
                                                {tableSettings.allowAddRows && !effectiveReadOnly && !isQuestionReadOnlyCollaborative(questionId) && (
                                                  <div className="mt-2">
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm btn-outline-primary"
                                                      onClick={addRow}
                                                      disabled={currentTableData.length >= (tableSettings.maxRows || 999)}
                                                    >
                                                      <i className="fa fa-plus me-1"></i>
                                                      {t('addRow', { ns: 'labels', defaultValue: 'Aggiungi Riga' })}
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })()}

                                          {/* Attachment area with tabs if multiple types are available */}
                                          {question.questionType !== 'table' && renderAttachmentArea(question, questionId)}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </form>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Side Panel Column - Show when guide or context data is available AND not completely collapsed */}
              {(shouldShowUserGuideVisible() || (shouldShowContextData() && !isContextCollapsed)) && (
                <div className="col-xl-4 col-lg-5">
                  <div className="sticky-top" style={{ top: `${toolbarHeight || (effectiveReadOnly ? 20 : 100)}px`, zIndex: 1020 }}>
                    {/* Context Data Display - Show when context data is available */}
                    {shouldShowContextData() && (
                      <div className="mb-3">
                        <ContextDataDisplay
                          contextData={formCompletion?.ContextData}
                          colors={colors}
                          isCollapsed={isContextCollapsed}
                          onToggleCollapse={toggleContextCollapse}
                          toolbarHeight={toolbarHeight || (effectiveReadOnly ? 20 : 100)}
                        />
                      </div>
                    )}

                    {/* User Guide Display - Show when guide is available */}
                    {shouldShowUserGuideVisible() && (
                      <UserGuideDisplay
                        form={form}
                        currentUser={currentUser}
                        colors={colors}
                        isSimulation={isSimulation}
                        isCollapsed={isGuideCollapsed}
                        onToggleCollapse={toggleGuideCollapse}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }
};

/**
 * Component Exports
 * 
 * Export the component with multiple names for backward compatibility:
 * - Form_Compiler: Main unified component
 * - Simulation_Builder: Alias for simulation mode usage
 */
export default Form_Compiler;
export { Form_Compiler as Simulation_Builder };
export { Form_Compiler };

